/* Linsoul Product Finder ("Find your sound").
 * Prototype engine (finder-engine v7, catalog-categories, pairing engine) and app,
 * forked so every customer-facing string comes from section settings (config.t) and
 * the catalog comes from live store data (sections/ls-finder-catalog.liquid). */
(function () {
  'use strict';

  const roots = Array.from(document.querySelectorAll('[data-ls-finder]'));
  const quizRoot = roots.find((r) => r.dataset.lsFinder === 'quiz');
  if (!quizRoot) return;

  /* ---------- Config + strings ---------- */
  const CFG = { t: {} };
  document.querySelectorAll('script[data-ls-finder-config]').forEach((node) => {
    try {
      const c = JSON.parse(node.textContent);
      Object.assign(CFG.t, c.t || {});
      delete c.t;
      CFG[node.dataset.lsFinderConfig] = c;
    } catch (_) {}
  });
  const Q = CFG.quiz || {};
  const T = CFG.t;
  const t = (key, vars) => {
    let s = T[key];
    if (s == null) return '';
    if (vars) s = String(s).replace(/\{(\w+)\}/g, (m, k) => (vars[k] != null ? vars[k] : m));
    return s;
  };
  const esc = (x) => String(x ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const valueLabels = {};
  String(Q.valueLabels || '').split(/\r?\n/).forEach((line) => {
    const i = line.indexOf('=');
    if (i > 0) valueLabels[line.slice(0, i).trim()] = line.slice(i + 1).trim();
  });
  const SETUP_KEYS = { 'Single Driver': 'single', 'Multi-driver / single technology': 'multi', Hybrid: 'hybrid', Tribrid: 'tribrid', 'Quadbrid+': 'quad' };
  const vl = (v) => valueLabels[v] || (SETUP_KEYS[v] && t('setup_' + SETUP_KEYS[v])) || v;

  const moneyCfg = Q.money || {};
  const currency = window.Shopify?.currency?.active || moneyCfg.currency || 'USD';
  const rate = Number(window.Shopify?.currency?.rate) || 1;
  const money = (n) => {
    const whole = Number.isInteger(Math.round(n * 100) / 100);
    try {
      return new Intl.NumberFormat(moneyCfg.locale || undefined, {
        style: 'currency', currency, minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2,
      }).format(n);
    } catch (_) {
      return String(n);
    }
  };

  /* ---------- Category contract (catalog-categories.js) ---------- */
  const C = (() => {
    const facets = {
      driverConfiguration: ['Balanced Armature', 'Dynamic Driver', 'Dynamic Driver Headphone', 'Hybrid', 'Planar', 'Quadbrid', 'Tribrid'],
      tuningProfile: ['Neutral', 'Reference', 'Meta', 'Harman 2019', 'U-Shaped', 'Mild V-Shaped', 'Sharp V-Shaped'],
      soundColor: ['Balanced', 'Bassy', 'Bassy & Bright', 'Bassy & Dark', 'Bright', 'Lean', 'Sterile', 'Warm'],
      recommendedUsage: ['Audiophile', 'Commuting', 'Gaming', 'Professional', 'Voice Calls'],
      musicGenre: ['Classical', 'Hip-Hop', 'Jazz', 'Pop', 'Vocal'],
      size: ['Small', 'Medium', 'Large'],
      cablePlug: ['2.5mm', '3.5mm', '4.4mm', 'Bluetooth', 'Modular', 'USB-C'],
      valueRating: ['Poor', 'Average', 'Good', 'Great', 'Excellent'],
      recommendation: ['Recommended for Audiophiles', 'Recommended for Bassheads', 'Recommended for Gamers', 'Recommended for Professionals'],
    };
    const fields = {
      driverConfiguration: 'driverConfiguration', tuningProfile: 'tuningProfile', soundColor: 'soundColors',
      recommendedUsage: 'recommendedUsage', musicGenre: 'musicGenres', size: 'size', cablePlug: 'cablePlugs',
      valueRating: 'performancePriceRatio', recommendation: 'linsoulRecommended',
    };
    const labels = {};
    Object.keys(fields).forEach((k) => { labels[k] = t('label_' + k) || k; });
    function values(p, key) {
      const v = p.categoryProfile?.[fields[key] || key];
      return Array.isArray(v) ? v : v ? [v] : [];
    }
    function normalize(f = {}) {
      const out = {};
      for (const k of Object.keys(fields)) {
        const v = f[k];
        if (typeof v === 'string' && facets[k].includes(v)) out[k] = v;
      }
      return out;
    }
    function matches(p, f = {}) {
      for (const k of Object.keys(fields)) if (f[k] && !values(p, k).includes(f[k])) return false;
      return true;
    }
    function soundText(p) {
      const c = p.categoryProfile;
      return c
        ? [c.tuningProfile, c.soundColors.join(' / ')].filter(Boolean).join(' | ')
        : [p.signature, ...(p.secondary || [])].filter(Boolean).map(vl).join(' / ');
    }
    function graphURL(p) {
      try {
        const u = new URL(p.categoryProfile?.graphLink);
        return u.protocol === 'https:' ? u.href : '';
      } catch (_) {
        return '';
      }
    }
    function cardFacts(p, expanded = false) {
      const c = p.categoryProfile;
      if (!c) return '';
      const keys = expanded ? Object.keys(fields) : ['tuningProfile', 'soundColor', 'driverConfiguration', 'size', 'cablePlug', 'valueRating', 'recommendation'];
      const rows = keys.map((k) => [labels[k], values(p, k).map(vl).join(', ')]).filter((r) => r[1]);
      if (!rows.length) return '';
      const graph = graphURL(p);
      return `<details class="lc-facts" ${expanded ? 'open' : ''}><summary>${esc(t('facts_summary'))}</summary><dl>${rows
        .map(([k, v]) => `<div><dt>${esc(k)}</dt><dd>${esc(v)}</dd></div>`)
        .join('')}</dl>${graph && t('facts_graph') ? `<a class="lc-graph" href="${esc(graph)}" target="_blank" rel="noopener noreferrer">${esc(t('facts_graph'))} <span aria-hidden="true">&#8599;</span></a>` : ''}${t('facts_note') ? `<p class="lc-fact-note">${esc(t('facts_note'))}</p>` : ''}</details>`;
    }
    return { facets, fields, labels, values, normalize, matches, soundText, cardFacts, graphURL };
  })();

  /* ---------- Budget bands (block settings, shop currency -> presentment) ---------- */
  const nice = (n) => {
    if (!Number.isFinite(n) || n < 10) return Number.isFinite(n) ? Math.round(n) : n;
    const step = 10 ** (Math.floor(Math.log10(n)) - 1);
    return Math.round(n / step) * step;
  };
  const converted = rate !== 1 || (moneyCfg.shopCurrency && moneyCfg.shopCurrency !== currency);
  const BUDGET_BANDS = Object.freeze(
    (Q.bands || [])
      .map((b) => {
        const min = Math.max(0, Number(b.min) || 0);
        const max = b.max === '' || b.max == null || !Number.isFinite(Number(b.max)) ? null : Number(b.max);
        const id = max === null ? 'over-' + min : min + '-' + max;
        const lmin = converted ? nice(min * rate) : min;
        const lmax = max === null ? null : converted ? nice(max * rate) : max;
        const auto = lmax === null ? t('band_over', { min: money(lmin) }) : t('band_range', { min: money(lmin), max: money(lmax) });
        return Object.freeze({ id, min: lmin, max: lmax, label: converted || !b.label ? auto : b.label, note: b.note || '' });
      })
      .filter((b) => b.max === null || b.max > b.min)
  );

  /* ---------- Engine (finder-engine.js v7) ---------- */
  const E = (() => {
    const WEIGHTS = { signature: 45, use: 30, genres: 15, build: 10 };
    const budgetBand = (id) => BUDGET_BANDS.find((b) => b.id === id) || null;
    const cents = (p) => Math.round(Number(p) * 100);
    function validateBudget(a) {
      const min = a.minBudget ?? 0;
      const max = a.maxBudget ?? null;
      if (!Number.isFinite(min) || min < 0) throw new TypeError('min');
      if (max !== null && (!Number.isFinite(max) || max <= 0 || max <= min)) throw new TypeError('max');
    }
    function inBudget(price, a = {}) {
      const min = a.minBudget ?? 0;
      const max = a.maxBudget ?? null;
      return Number.isFinite(price) && price > 0 && cents(price) > cents(min) && (max === null || cents(price) <= cents(max));
    }
    const VALID_SIGNATURES = ['Warm', 'Bright', 'Basshead', 'Reference', 'Balanced', 'Vocal'];
    const VALID_USES = ['Voice Calls', 'Gaming', 'Studio/monitor', 'Travel & Commute', 'Critical Listening (HiFi Audiophile)'];
    const SCOPE = { iems: ['Wired IEM'], headphones: ['Headphones'] };
    const CONFIGS = ['Single Driver', 'Multi-driver / single technology', 'Hybrid', 'Tribrid', 'Quadbrid', 'Multi-technology (5+)'];
    const SETUPS = ['Single Driver', 'Multi-driver / single technology', 'Hybrid', 'Tribrid', 'Quadbrid+'];
    const SETUP_GROUPS = { 'Quadbrid+': ['Quadbrid', 'Multi-technology (5+)'] };
    const SETUP_TECH_LIMIT = { 'Single Driver': 1, 'Multi-driver / single technology': 1, Hybrid: 2, Tribrid: 3, 'Quadbrid+': 4 };
    const configMatch = (p, c) => (SETUP_GROUPS[c] || [c]).includes(p.configuration);
    const TECH = ['Dynamic Driver', 'Balanced Armature', 'Electrostatic', 'Planar', 'Micro Planar', 'Piezoelectric', 'Bone Conductor', 'MEMS', 'Ribbon', 'AMT'];
    const HEADPHONE_TECH = ['Dynamic Driver', 'Electrostatic', 'Planar', 'Ribbon', 'AMT'];
    const ACOUSTICS = ['Open Back', 'Closed Back', 'Semi-open'];
    const RULES = { mainShare: 0.7, secondaryShare: 0.3, priorityDefault: 3, priorityStep: 2 };
    const unique = (x) => (Array.isArray(x) ? [...new Set(x.filter((v) => typeof v === 'string' && v))] : []);
    const GENRE_ALIAS = { 'Hip-Hop': 'Hip-Hop & Rap', Classical: 'Classical & Orchestral' };
    const USE_ALIAS = { Professional: 'Studio/monitor', Audiophile: 'Critical Listening (HiFi Audiophile)', Commuting: 'Travel & Commute' };
    function defaults() {
      return { scope: 'iems', minBudget: 0, maxBudget: null, genres: [], tuningProfile: '', soundColors: [], categoryFilters: {}, configuration: '', technology: [], acoustic: '', strictBuild: false, signatures: [], uses: [], signature: '', use: '', needsClosedBack: false, requireComplete: false };
    }
    function normalize(input = {}) {
      const a = { ...defaults(), ...input };
      a.scope = SCOPE[a.scope] ? a.scope : 'iems';
      a.genres = unique(unique(a.genres).map((v) => GENRE_ALIAS[v] || v)).slice(0, 3);
      a.technology = unique(a.technology).filter((x) => (a.scope === 'headphones' ? HEADPHONE_TECH : TECH).includes(x));
      a.uses = unique(a.uses).map((v) => USE_ALIAS[v] || v);
      a.use = USE_ALIAS[a.use] || a.use;
      a.requireComplete = a.requireComplete === true;
      a.signatures = unique(a.signatures?.length ? a.signatures : a.signature ? [a.signature] : []).filter((s) => VALID_SIGNATURES.includes(s)).slice(0, 2);
      a.uses = unique(a.uses?.length ? a.uses : a.use ? [a.use] : []).filter((u) => VALID_USES.includes(u)).slice(0, 2);
      a.tuningProfile = C.facets.tuningProfile.includes(a.tuningProfile) ? a.tuningProfile : '';
      a.soundColors = unique(a.soundColors).filter((v) => C.facets.soundColor.includes(v)).slice(0, 2);
      a.categoryFilters = C.normalize(a.categoryFilters);
      if (a.tuningProfile || a.soundColors.length) a.signatures = [];
      a.signature = a.signatures[0] || '';
      a.use = a.uses[0] || '';
      if (a.scope === 'headphones') a.configuration = '';
      if (a.scope === 'iems') { a.acoustic = ''; a.needsClosedBack = false; }
      return a;
    }
    function variantsFor(p, a) {
      return (p.variants || [])
        .filter((v) => v.finderEligible !== false && v.orderable === true && Number.isFinite(v.price) && v.price > 0 && inBudget(v.price, a))
        .sort((x, y) => x.price - y.price || x.label.localeCompare(y.label));
    }
    function buildStatus(p, input) {
      const a = normalize(input);
      const parts = [];
      const selected = a.technology || [];
      const anyOf = a.scope !== 'iems';
      if (a.configuration) parts.push(configMatch(p, a.configuration) ? 1 : 0);
      if (selected.length) {
        const count = selected.filter((x) => (p.technology || []).includes(x)).length;
        parts.push(anyOf ? (count > 0 ? 1 : 0) : count / selected.length);
      }
      if (a.acoustic) parts.push(p.acoustic === a.acoustic ? 1 : 0);
      return { requested: parts.length > 0, exact: parts.every((x) => x === 1), score: parts.length ? parts.reduce((n, x) => n + x, 0) / parts.length : 0, anyOf };
    }
    function exclusion(p, input) {
      const a = normalize(input);
      if (!(SCOPE[a.scope] || SCOPE.iems).includes(p.type)) return 'scope';
      if (!variantsFor(p, { ...a, minBudget: 0, maxBudget: null }).length) return 'orderable';
      if (!variantsFor(p, a).length) return 'budget';
      if (a.needsClosedBack && p.acoustic !== 'Closed Back') return 'closed';
      if (!C.matches(p, a.categoryFilters)) return 'category';
      if (a.strictBuild && !buildStatus(p, a).exact) return 'build';
      return '';
    }
    const preferenceParts = (values) => (values.length > 1 ? [RULES.mainShare, RULES.secondaryShare] : values.length ? [1] : []);
    function score(p, input) {
      const a = normalize(input);
      const values = {};
      const weights = {};
      const matched = [];
      const gaps = [];
      const core = [];
      const main = [];
      if (a.tuningProfile || a.soundColors.length) {
        weights.signature = WEIGHTS.signature;
        const requested = [...(a.tuningProfile ? [['tuningProfile', a.tuningProfile]] : []), ...a.soundColors.map((v) => ['soundColor', v])];
        values.signature = requested.reduce((sum, [k, v]) => {
          const ok = C.values(p, k).includes(v);
          core.push(ok);
          main.push(ok);
          if (ok) matched.push({ facet: C.labels[k], text: vl(v) });
          else gaps.push({ facet: C.labels[k], text: p.categoryProfile ? t('gap_not_tagged', { value: vl(v) }) : t('gap_unmapped') });
          return sum + (ok ? 1 : 0);
        }, 0) / requested.length;
      }
      if (a.signatures.length) {
        weights.signature = WEIGHTS.signature;
        const shares = preferenceParts(a.signatures);
        values.signature = a.signatures.reduce((sum, s, i) => {
          const v = p.signature === s ? 1 : (p.secondary || []).includes(s) ? (p.categoryProfile ? 1 : 0.7) : 0;
          core.push(v > 0);
          if (i === 0) main.push(v > 0);
          if (v) matched.push({ facet: t('facet_sound'), text: soundLabel(s) });
          else gaps.push({ facet: t('facet_sound'), text: p.signature ? t('gap_not_tagged', { value: soundLabel(s) }) : t('gap_unmapped') });
          return sum + v * shares[i];
        }, 0);
      }
      if (a.uses.length) {
        weights.use = WEIGHTS.use;
        const shares = preferenceParts(a.uses);
        values.use = a.uses.reduce((sum, u, i) => {
          const v = (p.uses || []).includes(u) ? 1 : 0;
          core.push(v > 0);
          if (i === 0) main.push(v > 0);
          if (v) matched.push({ facet: t('facet_use'), text: useLabel(u) });
          else gaps.push({ facet: t('facet_use'), text: t('gap_not_tagged', { value: useLabel(u) }) });
          return sum + v * shares[i];
        }, 0);
      }
      if (a.genres.length) {
        weights.genres = WEIGHTS.genres;
        const g = a.genres.filter((x) => (p.genres || []).includes(x));
        values.genres = g.length / a.genres.length;
        if (g.length) matched.push({ facet: t('facet_music'), text: g.map(genreLabel).join(' / ') });
        if (g.length < a.genres.length) gaps.push({ facet: t('facet_music'), text: (p.genres || []).length ? t('gap_some_genres') : t('gap_unmapped') });
      }
      const b = buildStatus(p, a);
      if (b.requested) {
        weights.build = WEIGHTS.build;
        values.build = b.score;
        if (b.exact) matched.push({ facet: t('facet_build'), text: [vl(a.configuration), a.technology.map(vl).join(b.anyOf ? ' / ' : ' + '), vl(a.acoustic)].filter(Boolean).join(' / ') });
        else gaps.push({ facet: t('facet_build'), text: t('gap_build') });
      }
      const totalWeight = Object.values(weights).reduce((x, y) => x + y, 0);
      const weighted = Object.keys(weights).reduce((n, k) => n + values[k] * weights[k], 0);
      const tier = core.every(Boolean) ? 3 : main.every(Boolean) ? 2 : 1;
      return { score: totalWeight ? weighted / totalWeight : 0, matched, gaps, hasPreferences: totalWeight > 0, buildExact: b.exact, values, tier };
    }
    function evidenceQuality(p) {
      let n = 0;
      if (p.signature) n++;
      if (p.categoryProfile) n++;
      if (p.configuration) n++;
      if ((p.technology || []).length) n++;
      return n;
    }
    function priorityValue(value) {
      const n = Number(value);
      return Number.isInteger(n) && n >= 1 && n <= 5 ? n : RULES.priorityDefault;
    }
    const merchantBoost = (value) => (priorityValue(value) - RULES.priorityDefault) * RULES.priorityStep;
    function rank(products, input = {}) {
      const a = normalize(input);
      validateBudget(a);
      const eligible = [];
      for (const p of products) {
        if (exclusion(p, a)) continue;
        const vs = variantsFor(p, a);
        const s = score(p, a);
        const priority = priorityValue(p.finderPriority);
        const boost = merchantBoost(priority);
        const requiredMatches = Object.entries(a.categoryFilters).map(([k, v]) => ({ facet: C.labels[k], text: C.labels[k] + ': ' + vl(v), required: true }));
        const fitType = s.hasPreferences ? (s.gaps.length ? 'partial' : 'complete') : requiredMatches.length || a.needsClosedBack || a.strictBuild ? 'requirements' : 'budget';
        eligible.push({ product: p, variant: vs[0], variants: vs, price: vs[0].price, evidenceQuality: evidenceQuality(p), ...s, matched: [...s.matched, ...requiredMatches], requiredMatches, fitType, priority, boost, rankPoints: s.score * 100 + boost });
      }
      eligible.sort((x, y) => y.tier - x.tier || y.rankPoints - x.rankPoints || y.score - x.score || (x.hasPreferences ? y.evidenceQuality - x.evidenceQuality : 0) || x.price - y.price || x.product.handle.localeCompare(y.product.handle));
      const preferences = Boolean(a.tuningProfile || a.soundColors.length || a.signatures.length || a.uses.length || a.genres.length || a.configuration || a.technology.length || a.acoustic);
      const preferenceMatches = preferences ? eligible.filter((r) => r.score > 0) : eligible;
      const complete = preferenceMatches.filter((r) => r.fitType !== 'partial');
      const matching = a.requireComplete ? complete : preferenceMatches;
      return { answers: a, eligible, matching, preferenceMatches, complete, partialCount: preferenceMatches.length - complete.length, hasPreferences: preferences, hasRequirements: Boolean(Object.keys(a.categoryFilters).length || a.needsClosedBack || a.strictBuild) };
    }
    function shortlist(result) {
      const list = result.matching;
      if (!list.length) return [];
      const best = list[0];
      const chosen = [{ ...best, label: best.fitType === 'partial' ? t('label_closest') : best.fitType === 'complete' ? t('label_recommended') : best.fitType === 'requirements' ? t('label_requirements') : t('label_budget') }];
      const peers = list.slice(1).filter((r) => r.tier === best.tier && (!best.hasPreferences || r.score >= best.score * 0.9));
      const cheaper = peers.filter((r) => cents(r.price) < cents(best.price)).sort((x, y) => x.price - y.price || y.rankPoints - x.rankPoints)[0];
      if (cheaper) chosen.push({ ...cheaper, label: cheaper.fitType === 'partial' ? t('label_cheaper_partial') : t('label_cheaper') });
      for (const r of peers) {
        if (chosen.length >= 3) break;
        if (!chosen.some((c) => c.product.handle === r.product.handle)) {
          chosen.push({ ...r, label: r.fitType === 'partial' ? t('label_another_partial') : result.hasPreferences ? t('label_another') : result.hasRequirements ? t('label_another_requirements') : t('label_another_budget') });
        }
      }
      return chosen;
    }
    function facets(products, input = {}) {
      const a = normalize(input);
      const bare = { ...a, configuration: '', technology: [], acoustic: '', strictBuild: false };
      const pool = products.filter((p) => !exclusion(p, bare));
      const universe = products.filter((p) => !exclusion(p, { ...bare, minBudget: 0, maxBudget: null, needsClosedBack: false }));
      const allowed = a.scope === 'headphones' ? HEADPHONE_TECH : TECH;
      const configurations = a.scope === 'headphones' ? [] : SETUPS.filter((c) => universe.some((p) => configMatch(p, c)) || a.configuration === c).map((value) => ({ value, count: pool.filter((p) => buildStatus(p, { ...a, configuration: value, technology: [] }).exact).length, selected: a.configuration === value }));
      const technologies = allowed.filter((x) => universe.some((p) => (p.technology || []).includes(x)) || a.technology.includes(x)).map((value) => {
        const tech = a.scope === 'iems' ? unique([...a.technology, value]) : [value];
        return { value, count: pool.filter((p) => buildStatus(p, { ...a, technology: tech }).exact).length, selected: a.technology.includes(value) };
      });
      const acoustics = a.scope === 'iems' ? [] : ACOUSTICS.filter((v) => universe.some((p) => p.acoustic === v) || a.acoustic === v).map((value) => ({ value, count: pool.filter((p) => buildStatus(p, { ...a, acoustic: value }).exact).length, selected: a.acoustic === value }));
      return { configurations, technologies, acoustics, pool, poolCount: pool.length, anyOf: a.scope !== 'iems' };
    }
    function categoryFacets(products, input = {}) {
      const a = normalize(input);
      const universe = products.filter((p) => !exclusion(p, { ...defaults(), scope: a.scope }));
      const pool = products.filter((p) => !exclusion(p, a));
      const out = {};
      for (const key of Object.keys(C.fields)) {
        const selected = key === 'tuningProfile' ? [a.tuningProfile] : key === 'soundColor' ? a.soundColors : [a.categoryFilters[key]];
        const filters = { ...a.categoryFilters };
        delete filters[key];
        const candidates = products.filter((p) => !exclusion(p, { ...a, categoryFilters: filters }));
        out[key] = C.facets[key].filter((v) => selected.includes(v) || universe.some((p) => C.values(p, key).includes(v))).map((value) => {
          const count = candidates.filter((p) => {
            if (!C.values(p, key).includes(value)) return false;
            if (key === 'tuningProfile') return a.soundColors.every((v) => C.values(p, 'soundColor').includes(v));
            if (key === 'soundColor') return (!a.tuningProfile || C.values(p, 'tuningProfile').includes(a.tuningProfile)) && a.soundColors.filter((v) => v !== value).every((v) => C.values(p, 'soundColor').includes(v));
            return true;
          }).length;
          return { value, count, selected: selected.includes(value) };
        });
      }
      return { fields: out, poolCount: pool.length, mappedCount: pool.filter((p) => p.categoryProfile).length, scopeMappedCount: universe.filter((p) => p.categoryProfile).length };
    }
    return { categoryFacets, budgetBand, validateBudget, VALID_SIGNATURES, VALID_USES, SCOPE, CONFIGS, SETUPS, SETUP_TECH_LIMIT, TECH, HEADPHONE_TECH, ACOUSTICS, defaults, normalize, variantsFor, buildStatus, exclusion, score, rank, shortlist, facets };
  })();

  /* ---------- Accessory pairing (inline PairingEngine) ---------- */
  const ACC = CFG.accessories || null;
  const GROUP_ICONS = { daps: 'dap', sources: 'desktop', adapters: 'plug', others: 'case' };
  const ACC_GROUPS = ACC
    ? (ACC.sources || []).reduce((out, s) => {
        if (s.label && !out.some((g) => g.key === s.group)) out.push({ key: s.group, label: s.label, sublabel: s.sublabel, desc: s.desc, icon: GROUP_ICONS[s.group] || 'case', limit: Number(s.limit) || 3 });
        return out;
      }, [])
    : [];
  const P = (() => {
    const cents = (n) => Math.round(Number(n) * 100);
    const intersects = (a = [], b = []) => a.some((x) => b.includes(x));
    const priority = (n) => (Number.isInteger(Number(n)) && Number(n) >= 1 && Number(n) <= 5 ? Number(n) : 3);
    const budgetCap = (answers = {}) => (answers.maxBudget == null ? null : Math.max(0, Math.round(Number(answers.maxBudget) * 100) / 100));
    function relevance(accessory, product, answers = {}) {
      const scope = product.type === 'Headphones' ? 'headphones' : 'iems';
      if (!(accessory.scopes || []).includes(scope)) return null;
      const plug = product.pairingProfile?.sourcePlugs || [];
      let points = 10;
      let detail = accessory.displayDetail;
      if (accessory.group === 'adapters') {
        const accepts = accessory.acceptsPlugs || [];
        if (accepts.length && plug.length && !intersects(accepts, plug)) return null;
        points = accepts.length && plug.length ? 24 : 10;
      }
      if (accessory.group === 'sources' || accessory.group === 'daps') {
        if (plug.length && (accessory.outputs || []).length && !intersects(plug, accessory.outputs)) return null;
        points = plug.length && intersects(plug, accessory.outputs || []) ? 24 : 18;
        const primary = (answers.uses || [])[0];
        if (primary === 'Travel & Commute' && ['dongle', 'dap'].includes(accessory.sourceKind)) points += 4;
        if (['Gaming', 'Studio/monitor', 'Critical Listening (HiFi Audiophile)'].includes(primary) && accessory.sourceKind === 'desktop') points += 4;
        if (accessory.outputs?.length) detail += (detail ? ' / ' : '') + accessory.outputs.join(' + ');
      }
      if (accessory.group === 'others') points = 20;
      return { points, detail };
    }
    function recommend(accessories, product, answers = {}, groupsCfg = []) {
      const cap = budgetCap(answers);
      const all = [];
      if (!product) return { cap, all, groups: [] };
      for (const a of accessories) {
        const variants = [];
        for (const v of a.variants || []) {
          if (!v.orderable || !Number.isFinite(v.price) || v.price <= 0 || (cap !== null && cents(v.price) > cents(cap))) continue;
          const fit = relevance(a, product, answers);
          if (fit) variants.push({ ...v, detail: fit.detail, contextPoints: fit.points });
        }
        variants.sort((x, y) => x.price - y.price || x.label.localeCompare(y.label));
        if (!variants.length) continue;
        const v = variants[0];
        all.push({ accessory: a, variant: v, variants, group: a.group, rankPoints: v.contextPoints + 2 * (priority(a.finderPriority) - 3) });
      }
      all.sort((a, b) => b.rankPoints - a.rankPoints || a.accessory.order - b.accessory.order || a.accessory.handle.localeCompare(b.accessory.handle));
      const groups = groupsCfg
        .map((g) => {
          const candidates = all.filter((x) => x.group === g.key);
          const count = Math.max(1, g.limit || 3);
          let items = candidates.slice(0, count);
          if (g.key === 'others') {
            const seen = new Set();
            const reps = [];
            for (const row of candidates) {
              const kind = row.accessory.otherKind || 'other';
              if (!seen.has(kind)) { seen.add(kind); reps.push(row); }
            }
            items = reps.slice(0, count);
            for (const row of candidates) {
              if (items.length >= count) break;
              if (!items.includes(row)) items.push(row);
            }
          }
          return { ...g, items, total: candidates.length };
        })
        .filter((g) => g.items.length);
      return { cap, all, groups };
    }
    return { recommend };
  })();

  /* ---------- Store data -> engine records ---------- */
  const list = (v) => (Array.isArray(v) ? v.filter((x) => typeof x === 'string' && x) : typeof v === 'string' && v ? [v] : []);
  const GENRE_FROM_STORE = { 'Hip-Hop': 'Hip-Hop & Rap', Classical: 'Classical & Orchestral', 'Vocal-focused music': 'Vocal' };
  const USE_FROM_STORE = { Professional: 'Studio/monitor', Audiophile: 'Critical Listening (HiFi Audiophile)', Commuting: 'Travel & Commute' };
  const PLUG_FROM_STORE = { '3.5 mm': '3.5mm', '4.4 mm': '4.4mm', '2.5 mm': '2.5mm', '6.35 mm': '6.35mm' };
  const SINGLE_TECH = { 'Dynamic Driver': 'Dynamic Driver', 'Dynamic Driver Headphone': 'Dynamic Driver', 'Balanced Armature': 'Balanced Armature', Planar: 'Planar' };
  const MIXED = ['Hybrid', 'Tribrid', 'Quadbrid'];
  const ROUTE_COLORS = ['Balanced', 'Bright', 'Warm'];
  const lower = (a) => (a || []).map((x) => String(x).trim().toLowerCase()).filter(Boolean);
  const iemTypes = lower(Q.iemTypes);
  const headphoneTypes = lower(Q.headphoneTypes);
  const excludeRe = (() => {
    const words = String(Q.excludeVariants || '').split('|').map((w) => w.trim()).filter(Boolean);
    return words.length ? new RegExp('\\b(?:' + words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|') + ')\\b', 'i') : null;
  })();
  const variantsOf = (r, eligible) =>
    (r.v || []).map(([label, price, available]) => ({
      label: String(label || ''),
      price: Number(price) / 100,
      orderable: Boolean(available),
      finderEligible: eligible ? !(excludeRe && excludeRe.test(String(label || ''))) : true,
    }));

  function productRecord(r) {
    const fmt = r.f === 'Wired IEM' || r.f === 'Headphones' ? r.f : null;
    const pt = String(r.t || '').trim().toLowerCase();
    const type = fmt || (iemTypes.includes(pt) ? 'Wired IEM' : headphoneTypes.includes(pt) ? 'Headphones' : null);
    if (!type) return null;
    const tp = r.tp || '';
    const sc = list(r.sc);
    const ru = list(r.ru);
    const mg = list(r.mg);
    const rb = list(r.rb);
    const plugs = list(r.pl).map((x) => PLUG_FROM_STORE[x] || x);
    if (r.mp) plugs.push('Modular');
    const hasCategory = Boolean(tp || sc.length || ru.length || mg.length);
    const cp = hasCategory
      ? { driverConfiguration: r.dc || '', tuningProfile: tp, soundColors: sc, recommendedUsage: ru, musicGenres: mg, size: r.sz || '', cablePlugs: plugs, performancePriceRatio: r.vr || '', linsoulRecommended: rb, graphLink: r.gl || '' }
      : null;
    let signature = r.ls || '';
    let secondary = list(r.ls2);
    if (!signature && cp) {
      const routes = sc.filter((x) => ROUTE_COLORS.includes(x));
      if (tp === 'Reference') routes.push('Reference');
      if (rb.includes('Recommended for Bassheads')) routes.push('Basshead');
      const u = [...new Set(routes)];
      signature = u[0] || '';
      secondary = u.slice(1);
    }
    const lg = list(r.lg);
    const lu = list(r.lu);
    const ldt = list(r.ldt);
    return {
      handle: r.h, name: r.n, brand: r.b, type, url: r.u, image: r.i || '',
      signature, secondary,
      genres: [...new Set((lg.length ? lg : mg).map((g) => GENRE_FROM_STORE[g] || g))],
      uses: [...new Set((lu.length ? lu : ru).map((u) => USE_FROM_STORE[u] || u))],
      configuration: r.ldc || (MIXED.includes(r.dc) ? r.dc : ''),
      technology: ldt.length ? ldt : SINGLE_TECH[r.dc] ? [SINGLE_TECH[r.dc]] : [],
      acoustic: '',
      finderPriority: r.fp,
      categoryProfile: cp,
      variants: variantsOf(r, true),
      pairingProfile: { sourcePlugs: plugs.filter((x) => x !== 'Modular') },
    };
  }

  function accessoryRecord(r, source, order) {
    return {
      handle: r.h, name: r.n, brand: r.b, url: r.u, image: r.i || '',
      group: source.group, sourceKind: source.kind, otherKind: source.kind,
      scopes: source.scope === 'both' ? ['iems', 'headphones'] : [source.scope],
      displayDetail: source.detail || '',
      outputs: [], acceptsPlugs: [], finderPriority: r.fp, order,
      variants: variantsOf(r, false),
    };
  }

  async function feedPage(base, page, sectionId) {
    const url = new URL(base, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('page', String(page));
    const res = await fetch(url.href, { credentials: 'same-origin' });
    if (!res.ok) throw new Error('feed ' + res.status);
    const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
    const node = doc.querySelector('[data-ls-finder-feed]');
    return node ? JSON.parse(node.textContent) : { page, pages: 1, items: [] };
  }

  async function loadFeed(base, { allPages = true } = {}) {
    const key = 'ls-finder-feed:' + base + ':' + currency + ':' + (allPages ? 'all' : '1');
    try {
      const hit = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (hit && Date.now() - hit.at < 10 * 60 * 1000) return hit.items;
    } catch (_) {}
    const sectionId = Q.feedSection || 'ls-finder-catalog';
    const first = await feedPage(base, 1, sectionId);
    let items = first.items || [];
    if (allPages && first.pages > 1) {
      const rest = await Promise.all(Array.from({ length: first.pages - 1 }, (_, i) => feedPage(base, i + 2, sectionId)));
      rest.forEach((p) => { items = items.concat(p.items || []); });
    }
    try { sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), items })); } catch (_) {}
    return items;
  }

  /* ---------- Icons ---------- */
  const iconPaths = {
    dap: '<rect x="5" y="2" width="14" height="20" rx="3"/><rect x="8" y="5" width="8" height="8" rx="1"/><path d="m10 16 4 2-4 2v-4Z"/>',
    iem: '<path d="M7 4c-3 1-4 5-2 8l4 4c2 2 5 0 5-3V8c0-4-4-6-7-4Z"/><path d="m14 9 5 1v5l-5 1M7 4C7 1 13 0 16 3m-7 13v4c0 3 7 3 7-1"/>',
    plug: '<path d="M9 2v5m6-5v5M7 7h10v5a5 5 0 0 1-10 0V7Zm5 10v5"/>',
    case: '<rect x="3" y="6" width="18" height="15" rx="4"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M3 12h18m-9-1v3"/>',
    cable: '<path d="M5 2v5m-2 0h4v5H3V7Zm2 5v5a4 4 0 0 0 8 0V7a3 3 0 0 1 6 0v5m-2 0h4v5h-4v-5Zm2 5v5"/>',
    dongle: '<rect x="6" y="10" width="12" height="12" rx="3"/><path d="M12 10V6m-3 0h6V2H9v4Zm0 9h6m-6 3h3"/>',
    desktop: '<rect x="2" y="6" width="20" height="12" rx="3"/><circle cx="17" cy="12" r="3"/><path d="M5 10h5m-5 4h2m-3 4v3m16-3v3"/>',
    arrow: '<path d="M4 12h15m-6-6 6 6-6 6"/>', back: '<path d="M20 12H5m6-6-6 6 6 6"/>', external: '<path d="M8 5h11v11M19 5 5 19"/>', check: '<path d="m5 12 4 4 10-10"/>',
    info: '<circle cx="12" cy="12" r="9"/><path d="M12 11v6m0-10h.01"/>', shield: '<path d="m12 3 8 3v5c0 5-8 10-8 10S4 16 4 11V6l8-3Z"/><path d="m8 12 3 3 5-6"/>',
    wallet: '<rect x="3" y="5" width="18" height="15" rx="3"/><path d="M3 8V5l14-2v2m4 6h-6v5h6m-3-2.5h.01"/>', music: '<path d="M9 18V5l11-2v13M9 8l11-2"/><ellipse cx="6" cy="18" rx="3" ry="2.5"/><ellipse cx="17" cy="16" rx="3" ry="2.5"/>',
    layers: '<path d="m12 3 9 5-9 5-9-5 9-5Zm-9 9 9 5 9-5m-18 5 9 5 9-5"/>', sound: '<path d="M4 9v6m4-10v14m4-16v18m4-14v10m4-8v6"/>',
    headphones: '<path d="M4 14v-3a8 8 0 0 1 16 0v3"/><rect x="3" y="12" width="4" height="9" rx="2"/><rect x="17" y="12" width="4" height="9" rx="2"/>',
    spark: '<path d="m12 3 2.5 6.5L21 12l-6.5 2.5L12 21l-2.5-6.5L3 12l6.5-2.5L12 3Z"/>', mic: '<rect x="9" y="2" width="6" height="13" rx="3"/><path d="M5 10v2a7 7 0 0 0 14 0v-2m-7 9v3m-4 0h8"/>',
    bolt: '<path d="m14 2-9 12h7l-2 8 9-12h-7l2-8Z"/>', guitar: '<path d="m14 10 6-7 2 2-7 6m-4-4c-2 0-4 1-4 3-4 0-6 4-4 7l4 4c3 2 7 0 7-4 2 0 3-2 3-4"/><circle cx="10" cy="14" r="2"/>',
    keys: '<rect x="3" y="4" width="18" height="16" rx="2"/><path d="M9 4v16m6-16v16M7 4v8h4V4m2 0v8h4V4"/>', note: '<path d="M13 18V4l7 3v5l-7-3"/><ellipse cx="9" cy="18" rx="4" ry="3"/>',
    disc: '<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="2"/><path d="M6 12a6 6 0 0 1 6-6m6 6a6 6 0 0 1-6 6"/>', film: '<rect x="3" y="4" width="18" height="16" rx="2"/><path d="M7 4v16m10-16v16M3 9h4m-4 6h4m10-6h4m-4 6h4"/>',
    wave: '<path d="M2 12c3-12 5 12 8 0s5 12 8 0 4 0 4 0"/>', sun: '<circle cx="12" cy="12" r="4"/><path d="M12 1v3m0 16v3M1 12h3m16 0h3M4 4l2 2m12 12 2 2M4 20l2-2M18 6l2-2"/>',
    bass: '<circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="4"/><path d="M2 4 4 2m16 0 2 2M2 20l2 2m16 0 2-2"/>',
    reference: '<path d="M3 7h18M3 12h18M3 17h18"/><circle cx="8" cy="7" r="2" fill="currentColor"/><circle cx="16" cy="12" r="2" fill="currentColor"/><circle cx="10" cy="17" r="2" fill="currentColor"/>',
    balance: '<path d="M12 3v18M5 7h14M4 19h16M5 7 2 14h6L5 7Zm14 0-3 7h6l-3-7Z"/>', game: '<path d="M7 7h10c4 0 6 13 2 13l-4-4H9l-4 4C1 20 3 7 7 7Z"/><path d="M6 10v5m-2-2.5h4m8-1h.01m3 2h.01M9 7l1-3h4l1 3"/>',
    studio: '<rect x="3" y="3" width="18" height="18" rx="3"/><path d="M7 7v10m5-10v10m5-10v10m-12-7h4m1 4h4m1-5h4"/>', travel: '<rect x="5" y="3" width="14" height="16" rx="4"/><path d="M5 11h14M8 15h.01m8 0h.01M8 19l-2 3m10-3 2 3M9 3V1h6v2"/>',
    ear: '<path d="M7 10a6 6 0 0 1 12 0c0 5-4 4-5 8s-6 5-7 0m4-6a3 3 0 1 1 4-4"/>', edit: '<path d="m4 16-1 5 5-1L20 8l-4-4L4 16ZM14 6l4 4"/>', refresh: '<path d="M20 7a9 9 0 1 0 1 9m-1-9V2m0 5h-5"/>',
    compare: '<rect x="3" y="4" width="7" height="16" rx="1"/><rect x="14" y="4" width="7" height="16" rx="1"/>', print: '<path d="M7 8V3h10v5M7 17H3V9h18v8h-4"/><path d="M7 14h10v7H7zM17 11h.01"/>',
    search: '<circle cx="10" cy="10" r="6"/><path d="m15 15 6 6"/>',
  };
  const icon = (name, cls = '') => (window.LsIcons && window.LsIcons.svg(window.LsIcons.has(name) ? name : 'sound', cls)) || `<svg class="${cls}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${iconPaths[name] || iconPaths.sound}</svg>`;

  /* ---------- Option lists (blocks) ---------- */
  const GENRES = (Q.genres || []).filter((g) => g.value);
  const SIGNATURES = (Q.sounds || []).filter((s) => E.VALID_SIGNATURES.includes(s.value));
  const USES = (Q.uses || []).filter((u) => E.VALID_USES.includes(u.value));
  const MIN_TAGGED = Math.max(0, Number(Q.minTagged) || 0);
  function hasTagged(kind, value) {
    if (!MIN_TAGGED || !ready) return true;
    const headphones = state.answers.scope === 'headphones';
    let n = 0;
    for (const p of DATA.products) {
      if ((p.type === 'Headphones') !== headphones) continue;
      const vals = kind === 'genre' ? p.genres : kind === 'use' ? p.uses : [p.signature, ...(p.secondary || [])];
      if ((vals || []).includes(value) && ++n >= MIN_TAGGED) return true;
    }
    return false;
  }
  const STEPS = [1, 2, 3, 4, 5].map((n) => t('step_' + n + '_label'));
  const genreLabel = (x) => GENRES.find((g) => g.value === x)?.label || vl(x);
  const useLabel = (x) => USES.find((u) => u.value === x)?.label || vl(x);
  const soundLabel = (x) => SIGNATURES.find((s) => s.value === x)?.label || vl(x);
  const scopeLabel = (x) => (x === 'headphones' ? t('scope_headphones_long') : t('scope_iems_long'));

  /* ---------- Mounts ---------- */
  const mount = (name) => roots.find((r) => r.dataset.lsFinder === name) || null;
  const quizMount = quizRoot.querySelector('[data-lsf-app]');
  const resultsRoot = mount('results');
  const accRoot = mount('accessories');
  const methodRoot = mount('method');
  const resultsMount = resultsRoot?.querySelector('[data-lsf-app]');
  const accMount = accRoot?.querySelector('[data-lsf-app]');
  const methodMount = methodRoot?.querySelector('[data-lsf-app]');
  const dialog = quizRoot.querySelector('#info-dialog');
  const announcer = quizRoot.querySelector('#lsf-announcer');
  const hasAbout = Boolean(CFG.method && (CFG.method.about || []).length);

  /* ---------- State ---------- */
  const KEY = 'ls-finder-session';
  let DATA = { products: [], accessories: null };
  let ready = false;
  let failed = false;
  let accLoading = false;
  let motionOK = false;
  if (window.gsap?.matchMedia) {
    window.gsap.matchMedia().add({ ok: '(prefers-reduced-motion: no-preference)' }, (ctx) => { motionOK = Boolean(ctx.conditions.ok); });
  }

  function initial() {
    return { answers: E.defaults(), step: 0, screen: 'quiz', budgetChoice: '', genreAny: false, buildMode: 'auto', signatureAny: false, useAny: false, completed: [], showAll: false, moreLimit: 9, editing: false, showUnavailable: false, notice: '', accessoryTab: '' };
  }
  let state = initial();
  let lastResult = null;
  let lastShortlist = [];
  let variantChoices = {};
  try {
    const saved = JSON.parse(sessionStorage.getItem(KEY) || 'null');
    if (saved && saved.version === 1 && saved.state) {
      const s = saved.state;
      state = { ...initial(), ...s, answers: E.normalize(s.answers) };
      state.step = Math.max(0, Math.min(4, Number(state.step) || 0));
      const band = E.budgetBand(state.budgetChoice);
      if (band) { state.answers.minBudget = band.min; state.answers.maxBudget = band.max; }
      else if (state.budgetChoice === 'custom') E.validateBudget(state.answers);
      else { state.answers.minBudget = 0; state.answers.maxBudget = null; state.budgetChoice = ''; state.step = 0; state.screen = 'quiz'; }
    }
  } catch (_) {
    state = initial();
  }
  if (!Q.showHeadphones) state.answers.scope = 'iems';
  const persist = () => { try { sessionStorage.setItem(KEY, JSON.stringify({ version: 1, state })); } catch (_) {} };
  const announce = (text) => { if (announcer) announcer.textContent = text; };
  const emit = (name, detail = {}) => window.dispatchEvent(new CustomEvent('linsoul:finder', { detail: { event: name, ...detail } }));
  const rank = (a) => E.rank(DATA.products, a);
  const skeleton = (rows = 3) => `<div class="lsf-skeleton" aria-hidden="true">${'<span></span>'.repeat(rows)}</div><p class="lsf-loading" role="status">${esc(failed ? t('load_error') : t('loading'))}</p>`;

  /* ---------- Quiz ---------- */
  function budgetText() {
    const band = E.budgetBand(state.budgetChoice);
    if (band) return band.label;
    if (!state.budgetChoice) return t('profile_budget');
    const a = state.answers;
    return a.maxBudget === null ? t('band_over', { min: money(a.minBudget || 0) }) : t('band_range', { min: money(a.minBudget || 0), max: money(a.maxBudget) });
  }
  function buildText() {
    const a = state.answers;
    return [vl(a.configuration), a.technology.map(vl).join(a.scope === 'iems' ? ' + ' : ' / '), vl(a.acoustic)].filter(Boolean).join(' · ') || t('setup_any');
  }
  function selectionText(field) {
    const a = state.answers;
    if (field === 'signatures' && (a.tuningProfile || a.soundColors.length)) {
      return [a.tuningProfile ? t('sel_tuning', { value: vl(a.tuningProfile) }) : '', a.soundColors.length ? t('sel_color', { value: a.soundColors.map(vl).join(' + ') }) : ''].filter(Boolean).join(' / ');
    }
    return (a[field] || []).map((x) => (field === 'uses' ? useLabel(x) : soundLabel(x))).join(' + ');
  }
  function valid() {
    const a = state.answers;
    if (state.step === 0) {
      try { E.validateBudget(a); return Boolean(E.budgetBand(state.budgetChoice) || state.budgetChoice === 'custom'); } catch (_) { return false; }
    }
    if (state.step === 1) return state.genreAny || a.genres.length > 0;
    if (state.step === 2) return true;
    if (state.step === 3) return state.signatureAny || Boolean(a.tuningProfile) || a.soundColors.length > 0 || a.signatures.length > 0;
    return state.useAny || a.uses.length > 0;
  }
  function choice({ group, value, title, description = '', glyph = '', checked = false, multi = false, badge = '' }) {
    return `<label class="choice"><input class="sr-only" type="${multi ? 'checkbox' : 'radio'}" name="lsf-${esc(group)}" value="${esc(value)}" data-bind="${esc(group)}" data-focus="${esc(group + '-' + value)}" ${checked ? 'checked' : ''}><span class="choice-inner">${glyph ? `<span class="choice-icon">${icon(glyph)}</span>` : ''}<span class="choice-copy"><span class="choice-title">${esc(title)}</span>${badge ? `<span class="preference-label">${esc(badge)}</span>` : ''}${description ? `<span class="choice-description">${esc(description)}</span>` : ''}</span><span class="choice-indicator" aria-hidden="true"></span></span></label>`;
  }
  function profile() {
    const a = state.answers;
    const items = [
      ['wallet', budgetText(), Boolean(state.budgetChoice)],
      ['music', state.genreAny ? t('profile_music_any') : a.genres.map(genreLabel).join(', ') || t('profile_music'), state.genreAny || a.genres.length],
      ['layers', state.completed.includes(2) || state.step >= 2 ? buildText() : t('profile_build'), state.completed.includes(2)],
      ['sound', selectionText('signatures') || (state.signatureAny ? t('profile_sound_any') : t('profile_sound')), a.signatures.length || a.tuningProfile || a.soundColors.length || state.signatureAny],
      ['headphones', selectionText('uses') || (state.useAny ? t('profile_use_any') : t('profile_use')), a.uses.length || state.useAny],
    ];
    return items.map(([i, text, v]) => `<div class="profile-row ${v ? 'filled' : ''}">${icon(i)}<span>${esc(text)}</span></div>`).join('');
  }
  function sidebar() {
    const bars = [9, 16, 26, 34, 22, 47, 66, 50, 77, 96, 80, 57, 73, 42, 28, 47, 33, 20, 12, 6];
    return `<aside class="sidebar" aria-label="${esc(t('profile_title'))}">${t('sidebar_eyebrow') ? `<div class="eyebrow"><span class="little-line"></span> ${esc(t('sidebar_eyebrow'))}</div>` : ''}<h2>${Q.sidebarHeading || ''}</h2>${t('sidebar_text') ? `<p>${esc(t('sidebar_text'))}</p>` : ''}<div class="sound-object" aria-hidden="true">${bars.map((h) => `<span style="height:${h}px"></span>`).join('')}</div><div class="profile"><div class="profile-top"><span>${esc(t('profile_title'))}</span><strong>${state.completed.length} / 5</strong></div>${profile()}</div>${t('sidebar_bottom') ? `<div class="sidebar-bottom">${icon('shield')} ${esc(t('sidebar_bottom'))}</div>` : ''}</aside>`;
  }
  function scopeControl() {
    const count = `<span class="scope-question-count">${esc(t('scope_count', { n: state.step + 1, total: 5 }))}</span>`;
    if (!Q.showHeadphones) return `<div class="scope-switch lsf-scope-single">${count}</div>`;
    const options = [['iems', t('scope_iems'), t('scope_iems_sub'), 'iem'], ['headphones', t('scope_headphones'), t('scope_headphones_sub'), 'headphones']];
    return `<fieldset class="scope-switch"><legend>${esc(t('scope_legend'))}</legend>${count}<div class="scope-buttons">${options
      .map(([value, title, sub, glyph]) => `<label class="scope-choice"><input class="sr-only" type="radio" name="lsf-scope" data-bind="scope" data-focus="scope-${value}" value="${value}" ${state.answers.scope === value ? 'checked' : ''}><span class="scope-choice-body"><span class="scope-glyph">${icon(glyph)}</span><span class="scope-copy"><strong>${esc(title)}</strong><small>${esc(sub)}</small></span><span class="scope-check">${icon('check')}</span></span></label>`)
      .join('')}</div></fieldset>`;
  }
  const COMPACT = Q.layout !== 'sidebar';
  function intro() {
    return `<header class="lsf-intro">${t('sidebar_eyebrow') ? `<p class="lsf-intro-eyebrow">${esc(t('sidebar_eyebrow'))}</p>` : ''}${Q.sidebarHeading ? `<h2 class="lsf-intro-title">${Q.sidebarHeading}</h2>` : ''}${t('sidebar_text') ? `<p class="lsf-intro-text">${esc(t('sidebar_text'))}</p>` : ''}</header>`;
  }
  function stepValue(i) {
    if (!COMPACT || !state.completed.includes(i) || i === state.step) return '';
    const a = state.answers;
    const text = [
      () => budgetText(),
      () => (state.genreAny ? t('profile_music_any') : a.genres.map(genreLabel).join(', ')),
      () => buildText(),
      () => selectionText('signatures') || (state.signatureAny ? t('profile_sound_any') : ''),
      () => selectionText('uses') || (state.useAny ? t('profile_use_any') : ''),
    ][i]();
    return text ? `<span class="step-value">${esc(text)}</span>` : '';
  }
  function stepper() {
    return `<nav class="stepper" aria-label="${esc(t('steps_label'))}">${STEPS.map((s, i) => `<button type="button" class="step-link ${i === state.step ? 'current' : state.completed.includes(i) ? 'done' : ''}" ${i === state.step ? 'aria-current="step"' : ''} data-action="step" data-step="${i}" ${i > state.step && !state.completed.includes(i) ? 'disabled' : ''}><span class="step-track"></span><span class="step-name"><span class="n">0${i + 1}</span>${esc(s)}</span>${stepValue(i)}</button>`).join('')}</nav>`;
  }
  function preferenceBadge(values, value) {
    const i = values.indexOf(value);
    return i === 0 ? t('badge_main') : i === 1 ? t('badge_also') : '';
  }
  function preferenceSummary(field) {
    const values = state.answers[field];
    if (values.length < 2) return '';
    const label = (x) => (field === 'uses' ? useLabel(x) : soundLabel(x));
    return `<div class="preference-summary"><span>${esc(t('pref_summary', { main: label(values[0]), also: label(values[1]) }))}</span><button type="button" class="text-button" data-action="make-main" data-field="${field}">${esc(t('pref_make_main', { also: label(values[1]) }))}</button></div>`;
  }
  function setMulti(field, value, checked, target) {
    const a = state.answers;
    if (checked && !a[field].includes(value)) {
      if (a[field].length >= 2) {
        target.checked = false;
        announce(t('pref_limit'));
        const err = document.getElementById('input-error');
        if (err) err.textContent = t('pref_limit');
        return false;
      }
      a[field].push(value);
    } else if (!checked) a[field] = a[field].filter((x) => x !== value);
    a.signature = a.signatures[0] || '';
    a.use = a.uses[0] || '';
    return true;
  }
  const TECH_CODES = {};
  String(t('tech_codes') || '').split(/\r?\n/).forEach((line) => {
    const i = line.indexOf('=');
    if (i > 0) TECH_CODES[line.slice(0, i).trim()] = line.slice(i + 1).trim();
  });
  const techCode = (v) => TECH_CODES[v] || vl(v).split(/\s+/).map((w) => w[0]).join('').slice(0, 3).toUpperCase();
  const setupDesc = { '': 'setup_any_desc', 'Single Driver': 'setup_single_desc', 'Multi-driver / single technology': 'setup_multi_desc', Hybrid: 'setup_hybrid_desc', Tribrid: 'setup_tribrid_desc', 'Quadbrid+': 'setup_quad_desc', 'Open Back': 'acoustic_open_desc', 'Closed Back': 'acoustic_closed_desc', 'Semi-open': 'acoustic_semi_desc' };
  function setupGlyph(value) {
    const dot = (x, y, r = 3) => `<circle cx="${x}" cy="${y}" r="${r}" fill="currentColor"/>`;
    const sq = (x, y, s = 5.8) => `<rect x="${x - s / 2}" y="${y - s / 2}" width="${s}" height="${s}" rx="1" fill="currentColor"/>`;
    const dia = (x, y) => `<path d="M${x} ${y - 3.9}l3.9 3.9-3.9 3.9-3.9-3.9Z" fill="currentColor"/>`;
    const tri = (x, y) => `<path d="M${x} ${y - 3.4}l3.6 6.2h-7.2Z" fill="currentColor"/>`;
    const ring = (x, y) => `<circle cx="${x}" cy="${y}" r="3" fill="none" stroke="currentColor" stroke-width="1.5"/>`;
    const art = {
      '': ring(7, 7) + ring(17, 7) + ring(7, 17) + ring(17, 17),
      'Single Driver': dot(12, 12, 5),
      'Multi-driver / single technology': dot(4.5, 12) + dot(12, 12) + dot(19.5, 12),
      Hybrid: dot(7.5, 12, 4) + sq(17, 12, 7.4),
      Tribrid: dot(4.5, 12) + sq(12, 12) + dia(19.6, 12),
      'Quadbrid+': dot(7, 7) + sq(17, 7) + dia(7, 17) + tri(17, 17.6),
      'Open Back': '<path d="M5 15V11a7 7 0 0 1 14 0v4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M4 14h3v6H4zM17 14h3v6h-3z" fill="none" stroke="currentColor" stroke-width="1.5" stroke-dasharray="2 1.6"/>',
      'Closed Back': '<path d="M5 15V11a7 7 0 0 1 14 0v4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M4 14h3v6H4zM17 14h3v6h-3z" fill="currentColor"/>',
      'Semi-open': '<path d="M5 15V11a7 7 0 0 1 14 0v4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M4 14h3v6H4z" fill="currentColor"/><path d="M17 14h3v6h-3z" fill="none" stroke="currentColor" stroke-width="1.5" stroke-dasharray="2 1.6"/>',
    };
    return `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">${art[value] || art['']}</svg>`;
  }
  function setupCard(value, count, checked) {
    const title = value ? vl(value) : t('setup_any');
    const meta = value ? (count ? t(count === 1 ? 'setup_count_one' : 'setup_count', { n: count }) : t('setup_none')) : '';
    const off = Boolean(value) && !count && !checked;
    return `<label class="lsf-setup${off ? ' is-off' : ''}"><input class="sr-only" type="radio" name="lsf-setup" value="${esc(value)}" data-bind="setup" data-focus="setup-${esc(value || 'any')}" ${checked ? 'checked' : ''} ${off ? 'disabled' : ''}><span class="lsf-setup-body"><span class="lsf-setup-art">${setupGlyph(value)}</span><span class="lsf-setup-title">${esc(title)}</span>${t(setupDesc[value]) ? `<span class="lsf-setup-desc">${esc(t(setupDesc[value]))}</span>` : ''}${meta ? `<span class="lsf-setup-count">${esc(meta)}</span>` : ''}</span></label>`;
  }
  function techLimit() {
    const a = state.answers;
    return a.scope === 'iems' ? E.SETUP_TECH_LIMIT[a.configuration] || 0 : 0;
  }
  function nextTech(value) {
    const sel = state.answers.technology;
    if (sel.includes(value)) return sel;
    const limit = techLimit();
    if (state.answers.scope !== 'iems') return [value];
    return limit && sel.length >= limit ? [...sel.slice(sel.length - limit + 1), value] : [...sel, value];
  }
  function buildAvailability() {
    const a = { ...state.answers, strictBuild: true };
    if (!a.configuration && !a.technology.length && !a.acoustic) return '';
    const n = rank(a).eligible.length;
    return n ? t(n === 1 ? 'build_count_one' : 'build_count', { n }) : t('build_zero');
  }
  let techShown = false;
  function setupStep() {
    if (!ready) return skeleton(6);
    const a = state.answers;
    const f = E.facets(DATA.products, a);
    const headphones = a.scope === 'headphones';
    const kinds = headphones ? f.acoustics : f.configurations;
    const current = headphones ? a.acoustic : a.configuration;
    const cards = kinds.length ? `<div class="lsf-setups${headphones ? ' lsf-setups--short' : ''}" role="radiogroup" aria-label="${esc(t('setup_label'))}">${setupCard('', f.poolCount, !current)}${kinds.map((k) => setupCard(k.value, k.count, k.selected)).join('')}</div>` : '';
    const showTech = headphones || Boolean(a.configuration) || !kinds.length;
    let swatches = '';
    if (showTech) {
      const options = f.technologies.map((x) => {
        const tech = nextTech(x.value);
        const count = x.selected ? null : f.pool.filter((p) => E.buildStatus(p, { ...a, technology: tech }).exact).length;
        return { ...x, count };
      }).filter((x) => x.selected || x.count > 0);
      const limit = techLimit();
      const hint = headphones || !kinds.length ? t('tech_hint_any') : limit === 1 ? t('tech_hint_one') : t('tech_hint_n', { n: limit });
      swatches = `<div class="lsf-tech${techShown ? '' : ' is-new'}"><div class="lsf-tech-head"><span class="lsf-tech-label" id="lsf-tech-label">${esc(t('tech_heading'))}</span>${hint ? `<span class="lsf-tech-hint">${esc(hint)}</span>` : ''}</div>${
        options.length
          ? `<div class="lsf-swatches" role="group" aria-labelledby="lsf-tech-label">${options.map((x) => `<label class="lsf-swatch"><input class="sr-only" type="checkbox" value="${esc(x.value)}" data-bind="technology" data-focus="technology-${esc(x.value)}" ${x.selected ? 'checked' : ''}><span class="lsf-swatch-body"><span class="lsf-swatch-code" aria-hidden="true">${esc(techCode(x.value))}</span><span class="lsf-swatch-name">${esc(vl(x.value))}</span>${x.count != null ? `<span class="lsf-swatch-count">${x.count}</span>` : ''}</span></label>`).join('')}</div>`
          : `<p class="lsf-tech-empty">${esc(t('tech_none'))}</p>`
      }</div>`;
    }
    techShown = showTech;
    const status = buildAvailability();
    return `${cards}${swatches}${status ? `<p class="lsf-setup-status" role="status">${icon('check')}<span>${esc(status)}</span></p>` : !current && t('setup_any_note') ? `<p class="helper">${icon('info')} ${esc(t('setup_any_note'))}</p>` : ''}`;
  }
  function categoryBuild() {
    if (!ready) return '';
    const a = state.answers;
    const f = E.categoryFacets(DATA.products, a);
    const keys = ['size', 'cablePlug', 'valueRating', 'recommendation'].filter((k) => f.fields[k].length);
    if (!keys.length) return '';
    return `<details class="lc-refinements lc-finder-refinements" ${Object.keys(a.categoryFilters).length ? 'open' : ''}><summary>${esc(t('cat_summary'))}</summary>${t('cat_intro') ? `<p>${esc(t('cat_intro'))}</p>` : ''}<div class="lc-filter-grid">${keys
      .map((k) => `<label>${esc(C.labels[k])}<select data-bind="category-filter" data-category="${k}" data-focus="category-${k}"><option value="">${esc(t('cat_any'))}</option>${f.fields[k].map((x) => `<option value="${esc(x.value)}" ${x.selected ? 'selected' : ''} ${!x.count && !x.selected ? 'disabled' : ''}>${esc(vl(x.value))} (${x.count})</option>`).join('')}</select></label>`)
      .join('')}</div>${t('cat_note') ? `<p class="lc-fact-note">${esc(t('cat_note'))}</p>` : ''}${Object.keys(a.categoryFilters).length ? `<p class="finder-requirements-count" role="status">${esc(t('cat_count', { n: f.poolCount }))}</p>` : ''}</details>`;
  }
  function soundChoices() {
    const a = state.answers;
    const legacy = `<div class="option-grid sound-grid" role="group" aria-label="${esc(t('legacy_summary'))}">${SIGNATURES.filter((s) => a.signatures.includes(s.value) || hasTagged('sound', s.value)).map((s) => choice({ group: 'signature', value: s.value, title: s.label, multi: true, glyph: s.icon, description: s.desc, checked: a.signatures.includes(s.value), badge: preferenceBadge(a.signatures, s.value) })).join('')}</div>${preferenceSummary('signatures')}`;
    if (!ready) return skeleton(4);
    const f = E.categoryFacets(DATA.products, a);
    const mapped = f.scopeMappedCount > 0;
    const count = (n) => t(n === 1 ? 'count_one' : 'count_many', { n });
    const opts = (key, group, multi = false) => f.fields[key].map((x) => choice({ group, value: x.value, title: vl(x.value), multi, checked: x.selected, badge: count(x.count) })).join('');
    const tagged = mapped || a.tuningProfile || a.soundColors.length
      ? `<section class="lc-sound-section"><h2>${esc(t('tuning_heading'))}</h2>${t('tuning_text') ? `<p>${esc(t('tuning_text'))}</p>` : ''}<div class="option-grid" role="group" aria-label="${esc(t('tuning_heading'))}">${opts('tuningProfile', 'tuning-profile')}${choice({ group: 'tuning-profile', value: '', title: t('tuning_any'), checked: !a.tuningProfile })}</div></section><section class="lc-sound-section"><h2>${esc(t('color_heading'))}</h2>${t('color_text') ? `<p>${esc(t('color_text'))}</p>` : ''}<div class="option-grid" role="group" aria-label="${esc(t('color_heading'))}">${opts('soundColor', 'sound-color', true)}</div></section>${t('sound_coverage') ? `<p class="finder-coverage-note">${esc(t('sound_coverage', { mapped: f.mappedCount, pool: f.poolCount, scope: scopeLabel(a.scope) }))}</p>` : ''}`
      : t('sound_unmapped') ? `<p class="finder-coverage-note">${esc(t('sound_unmapped'))}</p>` : '';
    const groups = SIGNATURES.length
      ? `<details class="lc-refinements" ${!mapped || a.signatures.length ? 'open' : ''}><summary>${esc(t('legacy_summary'))}</summary>${t('legacy_text') ? `<p>${esc(t('legacy_text'))}</p>` : ''}${legacy}</details>`
      : '';
    return `${tagged}${groups}<div class="secondary-row"><button type="button" class="option-link ${state.signatureAny ? 'selected' : ''}" data-action="signature-any" data-focus="signature-any">${esc(t('sound_any'))}</button><span class="footer-note">${esc(t('color_count', { n: a.soundColors.length }))}</span></div>${t('sound_helper') ? `<p class="helper">${icon('info')} ${esc(t('sound_helper'))}</p>` : ''}`;
  }
  function bodyForStep() {
    const a = state.answers;
    const n = state.step + 1;
    const head = { meta: t('step_' + n + '_meta'), title: t('step_' + n + '_title'), desc: t('step_' + n + '_desc'), pill: t('step_' + n + '_pill') };
    if (state.step === 0) {
      const custom = state.budgetChoice === 'custom' ? choice({ group: 'budget', value: 'custom', title: budgetText(), description: t('budget_custom_desc'), checked: true }) : '';
      return { ...head, body: `<div class="option-grid budget-grid range-grid" role="group" aria-label="${esc(head.title)}">${custom}${BUDGET_BANDS.map((b) => choice({ group: 'budget', value: b.id, title: b.label, description: b.note, checked: state.budgetChoice === b.id })).join('')}</div>${t('budget_note') ? `<p class="helper">${icon('info')} ${esc(t('budget_note'))}</p>` : ''}` };
    }
    if (state.step === 1) {
      return { ...head, body: `<div class="option-grid genre-grid" role="group" aria-label="${esc(head.title)}">${GENRES.filter((g) => a.genres.includes(g.value) || hasTagged('genre', g.value)).map((g) => choice({ group: 'genre', value: g.value, title: g.label, glyph: g.icon, multi: true, checked: a.genres.includes(g.value) })).join('')}</div><div class="secondary-row"><button type="button" class="option-link ${state.genreAny ? 'selected' : ''}" data-action="genre-any" data-focus="genre-any">${esc(t('genre_any'))}</button><span class="footer-note">${esc(t('genre_count', { n: a.genres.length }))}</span></div>` };
    }
    if (state.step === 2) {
        if (a.scope === 'headphones') {
        const designs = !ready || E.facets(DATA.products, a).acoustics.length > 0;
        const key = designs ? 'headphones' : 'drivers';
        if (t('step_3_title_' + key)) head.title = t('step_3_title_' + key);
        if (t('step_3_desc_' + key)) head.desc = t('step_3_desc_' + key);
      }
      return {
        ...head,
        body: setupStep() + categoryBuild(),
      };
    }
    if (state.step === 3) return { ...head, body: soundChoices() };
    return {
      ...head,
      body: `<div class="option-grid use-grid" role="group" aria-label="${esc(head.title)}">${USES.filter((u) => a.uses.includes(u.value) || hasTagged('use', u.value)).map((u) => choice({ group: 'use', value: u.value, title: u.label, description: u.desc, glyph: u.icon, multi: true, checked: a.uses.includes(u.value), badge: preferenceBadge(a.uses, u.value) })).join('')}</div>${preferenceSummary('uses')}<div class="secondary-row"><button type="button" class="option-link ${state.useAny ? 'selected' : ''}" data-action="use-any" data-focus="use-any">${esc(t('use_any'))}</button><span class="footer-note">${esc(t('use_count', { n: a.uses.length }))}</span></div>${
        a.scope !== 'iems' && (a.uses.includes('Travel & Commute') || a.needsClosedBack) && t('closed_label')
          ? `<label class="strict-label isolation-rule"><input type="checkbox" data-bind="closed-back" data-focus="closed-back" ${a.needsClosedBack ? 'checked' : ''}><span>${esc(t('closed_label'))}<small>${esc(t('closed_note'))}</small></span></label>`
          : ''
      }${t('use_helper') ? `<p class="helper">${icon('info')} ${esc(t('use_helper'))}</p>` : ''}`,
    };
  }
  function renderQuiz() {
    const q = bodyForStep();
    const next = state.editing ? t('next_update') : state.step === 4 ? t('next_last') : t('next');
    quizMount.innerHTML = `<div class="finder-layout${COMPACT ? ' lsf-compact' : ''}">${COMPACT ? '' : sidebar()}<section class="workspace" aria-label="${esc(t('workspace_label'))}">${COMPACT ? `<div class="lsf-top">${intro()}${scopeControl()}</div>` : scopeControl()}${state.notice ? `<p class="context-notice" role="status">${esc(state.notice)}</p>` : ''}${stepper()}<div class="quiz-card"><div class="question-meta"><span class="eyebrow">${esc(q.meta)}</span>${q.pill ? `<span class="step-pill">${esc(q.pill)}</span>` : ''}</div><h1 id="question-title" tabindex="-1">${esc(q.title)}</h1>${q.desc ? `<p class="question-desc">${esc(q.desc)}</p>` : ''}<div class="question-body">${q.body}<p class="error-text" id="input-error" role="status"></p></div><div class="question-footer">${state.step ? `<button type="button" class="back-button" data-action="back">${icon('back')} ${esc(t('back'))}</button>` : `<span class="footer-note">${esc(t('footer_start'))}</span>`}<button type="button" class="button" id="next-button" data-action="next" ${valid() ? '' : 'disabled'}>${esc(next)}${icon('arrow')}</button></div></div><div class="below-card">${t('below_card') ? `<span>${icon('shield')} ${esc(t('below_card'))}</span>` : '<span></span>'}<span class="lsf-below-actions">${hasAbout && t('how_it_works') ? `<button type="button" class="text-button" data-action="about">${esc(t('how_it_works'))}</button>` : ''}<button type="button" class="text-button" data-action="reset">${esc(t('reset'))}</button></span></div></section></div>`;
  }

  /* ---------- Results ---------- */
  function matchingReasons(r) {
    const rows = r.matched.map((m) => t('why_line', { facet: m.facet, text: m.text }));
    if (!r.hasPreferences && !rows.length) return [t('why_budget'), t('why_type')].filter(Boolean);
    return [...new Set(rows)].slice(0, 4);
  }
  function caution(r) {
    const a = state.answers;
    const notes = [];
    if (r.gaps.length) notes.push(t('why_line', { facet: r.gaps[0].facet, text: r.gaps[0].text }) + '.');
    if (a.uses.includes('Travel & Commute')) notes.push(['Open Back', 'Semi-open'].includes(r.product.acoustic) ? t('caution_travel_open') : t('caution_travel'));
    if (a.uses.includes('Voice Calls')) notes.push(t('caution_calls'));
    else if (a.uses.includes('Gaming')) notes.push(t('caution_gaming'));
    if (a.categoryFilters.cablePlug) notes.push(t('caution_plug'));
    if (a.uses.includes('Studio/monitor')) notes.push(t('caution_studio'));
    const out = notes.filter(Boolean);
    if (!out.length && t('caution_default')) out.push(t('caution_default'));
    return out.slice(0, 3).join(' ');
  }
  const fitLabel = (r) => t('fit_' + r.fitType);
  function fitDisclosure(r) {
    return `<div class="finder-match-status ${r.fitType === 'partial' ? 'is-partial' : ''}" data-fit="${r.fitType}"><strong>${esc(fitLabel(r))}</strong>${r.fitType === 'partial' ? `<span>${r.gaps.map((g) => esc(t('why_line', { facet: g.facet, text: g.text }))).join('<br>')}</span>` : ''}</div>`;
  }
  const chosenVariant = (r) => {
    const i = variantChoices[r.product.handle];
    return Number.isInteger(i) ? r.variants[i] || r.variant : r.variant;
  };
  const optionLabel = (label) => (label === 'Default Title' ? t('card_option_default') : label);
  function productCard(r, index) {
    const p = r.product;
    const v = chosenVariant(r);
    const desc = [C.soundText(p) || t('card_sound_unmapped'), vl(p.configuration || p.categoryProfile?.driverConfiguration || '')].filter(Boolean).join(' / ');
    const reasons = matchingReasons(r);
    const before = caution(r);
    return `<article class="product-card"><div class="product-ribbon"><strong>${icon(index === 0 ? 'spark' : 'headphones')}${esc(r.label)}</strong><span>0${index + 1}</span></div><a class="product-image" href="${esc(p.url)}" tabindex="-1" aria-hidden="true"><span class="image-fallback">${icon(p.type === 'Headphones' ? 'headphones' : 'ear')}</span>${p.image ? `<img src="${esc(p.image)}" alt="" loading="lazy" decoding="async">` : ''}</a><div class="product-main"><p class="product-brand">${esc(p.brand)}</p><h2 class="product-title">${esc(p.name)}</h2><p class="product-subtitle">${esc(desc)}</p>${fitDisclosure(r)}${C.cardFacts(p)}<div class="price-line"><div class="price" id="lsf-price-${esc(p.handle)}">${t('card_price') ? `<small>${esc(t('card_price'))}</small>` : ''}${esc(money(v.price))}</div>${t('card_in_range') ? `<span class="budget-badge">${esc(t('card_in_range'))}</span>` : ''}</div><div><p class="why-title">${esc(r.hasPreferences ? t('card_why') : t('card_why_budget'))}</p>${reasons.map((x) => `<div class="match-line">${icon('check')}<span>${esc(x)}</span></div>`).join('')}</div>${before ? `<p class="trade-off"><strong>${esc(t('card_before'))}</strong> ${esc(before)}</p>` : ''}${
      r.variants.length > 1
        ? `<div class="variant-block"><label for="lsf-variant-${esc(p.handle)}">${esc(t('card_option'))}</label><select id="lsf-variant-${esc(p.handle)}" data-bind="variant" data-handle="${esc(p.handle)}">${r.variants.map((vv, i) => `<option value="${i}" ${vv === v ? 'selected' : ''}>${esc(optionLabel(vv.label))} &middot; ${esc(money(vv.price))}</option>`).join('')}</select><span class="variant-print" id="lsf-variant-print-${esc(p.handle)}">${esc(optionLabel(v.label))}</span></div>`
        : ''
    }<a class="button product-cta ${index === 0 ? 'gold' : 'outline'}" href="${esc(p.url)}" data-track-product="${esc(p.handle)}">${esc(t('card_cta'))}${icon('arrow')}</a><button type="button" class="details-button" data-action="product-detail" data-handle="${esc(p.handle)}">${esc(t('card_details'))}</button></div></article>`;
  }
  function answersStrip() {
    const a = state.answers;
    const pills = [
      [0, budgetText(), 'wallet'],
      [1, state.genreAny ? t('strip_all_music') : a.genres.map(genreLabel).join(', '), 'music'],
      [2, buildText(), 'layers'],
      [3, selectionText('signatures') || t('strip_any_sound'), 'sound'],
      [4, selectionText('uses') || t('strip_any_use'), 'headphones'],
    ];
    return `<div class="answer-strip">${t('recipe') ? `<span class="caption">${esc(t('recipe'))}</span>` : ''}${Q.showHeadphones ? `<button type="button" class="answer-pill" data-action="change-scope">${icon('headphones')}${esc(scopeLabel(a.scope))}</button>` : ''}${pills
      .map(([n, text, i]) => `<button type="button" class="answer-pill" data-action="edit-step" data-step="${n}" aria-label="${esc(t('strip_edit', { step: STEPS[n], value: text }))}">${icon(i)}${esc(text)}${icon('edit')}</button>`)
      .join('')}${a.needsClosedBack ? `<button type="button" class="answer-pill" data-action="edit-step" data-step="4">${esc(t('strip_closed'))}</button>` : ''}${Object.entries(a.categoryFilters)
      .map(([k, v]) => `<button type="button" class="answer-pill" data-action="edit-step" data-step="2">${esc(C.labels[k])}: ${esc(vl(v))}</button>`)
      .join('')}<span class="result-count">${esc(t('strip_count', { n: lastResult.eligible.length }))}</span></div>`;
  }
  function compactCard(r) {
    const p = r.product;
    return `<article class="compact-product">${p.image ? `<img src="${esc(p.image)}" alt="" loading="lazy">` : '<span></span>'}<div class="compact-copy"><h3>${esc(p.name)}</h3><p>${esc(C.soundText(p) || t('card_sound_unmapped'))} &middot; ${esc(money(r.price))}</p><span class="finder-compact-status">${esc(fitLabel(r))}</span><button type="button" class="text-button" data-action="product-detail" data-handle="${esc(p.handle)}">${esc(t('card_details'))}</button><a href="${esc(p.url)}" data-track-product="${esc(p.handle)}">${esc(t('card_cta'))}</a></div></article>`;
  }
  function emptyHTML() {
    const a = state.answers;
    const hasEligible = lastResult.eligible.length > 0;
    const complete = a.requireComplete && lastResult.partialCount > 0;
    const kind = complete ? 'complete' : hasEligible ? 'tagged' : 'none';
    const btn = (action, label, extra = '') => (label ? `<button type="button" class="button outline" data-action="${action}" ${extra}>${esc(label)}</button>` : '');
    return `<section class="empty-state"><div class="empty-icon">${icon('search')}</div><h2>${esc(t('empty_' + kind + '_title'))}</h2><p>${esc(t('empty_' + kind + '_text'))}</p><div class="empty-actions">${complete && t('empty_allow_partial') ? `<button type="button" class="button" data-action="allow-partial">${esc(t('empty_allow_partial'))}</button>` : ''}${Object.keys(a.categoryFilters).length ? btn('edit-step', t('empty_edit_categories'), 'data-step="2"') : ''}${a.needsClosedBack ? btn('edit-step', t('empty_edit_closed'), 'data-step="4"') : ''}${a.strictBuild ? btn('relax-build', t('empty_relax_build')) : ''}${btn('edit-step', t('empty_change_sound'), 'data-step="3"')}${btn('edit-step', t('empty_edit_budget'), 'data-step="0"')}${hasEligible ? btn('browse-budget', t('empty_clear_prefs')) : ''}</div></section>`;
  }
  function resultsLead() {
    if (!lastShortlist.length) return t('res_text_empty');
    if (lastResult.hasPreferences) return lastShortlist[0].fitType === 'partial' ? t('res_text_partial') : t('res_text_match');
    return lastResult.hasRequirements ? t('res_text_requirements') : t('res_text_budget');
  }
  function renderResults() {
    if (!resultsMount) return;
    if (!ready) {
      resultsMount.innerHTML = `<div class="results-page"><div class="results-intro"><div>${t('res_eyebrow') ? `<div class="eyebrow">${esc(t('res_eyebrow'))}</div>` : ''}<h1 id="question-title" tabindex="-1">${esc(t('res_title'))}</h1></div></div>${skeleton(3)}</div>`;
      return;
    }
    lastResult = rank(state.answers);
    lastShortlist = E.shortlist(lastResult);
    const more = lastResult.matching.filter((r) => !lastShortlist.some((s) => s.product.handle === r.product.handle));
    const counts = lastResult.hasPreferences
      ? t('ctl_counts', { complete: lastResult.complete.length, partial: lastResult.partialCount })
      : t('ctl_options', { n: lastResult.matching.length });
    const toolbar = [
      t('share') ? `<button type="button" class="button outline" data-action="share-finder">${esc(t('share'))}</button>` : '',
      lastShortlist.length > 1 && t('compare') ? `<button type="button" class="button outline" data-action="compare">${icon('compare')} ${esc(t('compare'))}</button>` : '',
      lastShortlist.length && t('print') ? `<button type="button" class="button outline" data-action="print">${icon('print')} ${esc(t('print'))}</button>` : '',
    ].join('');
    resultsMount.innerHTML = `<div class="results-page"><div class="results-intro"><div>${t('res_eyebrow') ? `<div class="eyebrow">${esc(t('res_eyebrow'))}</div>` : ''}<h1 id="question-title" tabindex="-1">${esc(lastShortlist.length ? t('res_title') : t('res_title_empty'))}</h1><p>${esc(resultsLead())}</p></div><div class="result-toolbar">${toolbar}</div></div>${answersStrip()}<div class="finder-result-controls"><p role="status">${esc(counts)}</p>${lastResult.hasPreferences ? `<label><input type="checkbox" data-bind="require-complete" data-focus="require-complete" ${state.answers.requireComplete ? 'checked' : ''}>${esc(t('ctl_complete_only'))}</label>` : ''}</div>${t('res_notice') ? `<p class="preview-notice">${icon('info')}<span>${esc(t('res_notice'))}</span></p>` : ''}${lastShortlist.length ? `<div class="products-grid">${lastShortlist.map(productCard).join('')}</div>` : emptyHTML()}<div class="results-bottom">${more.length ? `<button type="button" class="button outline" data-action="show-more">${esc(t(state.showAll ? 'more_hide' : 'more_show', { n: more.length }))}${icon('arrow')}</button>` : ''}<button type="button" class="text-button" data-action="reset">${icon('refresh')} ${esc(t('restart'))}</button></div>${
      state.showAll && more.length
        ? `<section class="more-options"><div class="section-heading"><h2>${esc(t('more_heading'))}</h2>${t('more_text') ? `<span>${esc(t('more_text'))}</span>` : ''}</div><div class="compact-grid">${more.slice(0, state.moreLimit).map(compactCard).join('')}</div>${more.length > state.moreLimit ? `<div class="results-bottom"><button type="button" class="button outline" data-action="load-more">${esc(t('load_more'))}</button></div>` : ''}</section>`
        : ''
    }</div>`;
    emit('results_view', { scope: state.answers.scope, eligible: lastResult.eligible.length, shortlist: lastShortlist.map((r) => r.product.handle) });
  }

  /* ---------- Accessories ---------- */
  let lastPairResult = null;
  function accessoryCard(row, group) {
    const a = row.accessory;
    const v = row.variant;
    const varied = new Set(row.variants.map((x) => Math.round(x.price * 100))).size > 1;
    return `<article class="accessory-card"><a class="accessory-link" href="${esc(a.url)}" data-track-pairing="${esc(a.handle)}"><div class="accessory-image"><span class="image-fallback">${icon(group.icon)}</span>${a.image ? `<img src="${esc(a.image)}" alt="" loading="lazy" decoding="async">` : ''}</div><div class="accessory-copy"><p class="product-brand">${esc(a.brand)}</p><h4>${esc(a.name)}</h4>${v.detail ? `<p class="accessory-detail">${esc(v.detail)}</p>` : ''}<div class="accessory-bottom"><strong>${varied && t('acc_from') ? `<small>${esc(t('acc_from'))} </small>` : ''}${esc(money(v.price))}</strong><span aria-hidden="true">${icon('arrow')}</span></div></div></a></article>`;
  }
  async function loadAccessories() {
    if (!ACC || DATA.accessories || accLoading) return;
    accLoading = true;
    const sources = (ACC.sources || []).filter((s) => s.url);
    const lists = await Promise.all(sources.map((s) => loadFeed(s.url, { allPages: false }).catch(() => [])));
    const seen = new Set();
    const out = [];
    lists.forEach((items, si) => {
      items.forEach((r, i) => {
        const key = sources[si].group + ':' + r.h;
        if (seen.has(key)) return;
        seen.add(key);
        out.push(accessoryRecord(r, sources[si], si * 1000 + i));
      });
    });
    DATA.accessories = out;
    accLoading = false;
    renderAccessories();
  }
  function renderAccessories() {
    if (!accMount) return;
    const main = lastShortlist[0];
    if (state.screen !== 'results' || !ready || !main) { accMount.innerHTML = ''; return; }
    if (!DATA.accessories) {
      accMount.innerHTML = `<div class="results-page"><section class="accessories-section">${skeleton(3)}</section></div>`;
      loadAccessories();
      return;
    }
    lastPairResult = P.recommend(DATA.accessories, main.product, state.answers, ACC_GROUPS);
    const result = lastPairResult;
    const cap = result.cap;
    if (!result.groups.some((g) => g.key === state.accessoryTab)) state.accessoryTab = result.groups[0]?.key || '';
    const tabs = result.groups.map((g) => `<button type="button" class="accessory-tab" role="tab" id="lsf-acc-tab-${g.key}" aria-controls="lsf-acc-panel-${g.key}" aria-selected="${state.accessoryTab === g.key}" tabindex="${state.accessoryTab === g.key ? '0' : '-1'}" data-action="accessory-tab" data-category="${g.key}">${icon(g.icon)}<span>${esc(g.label)}${g.sublabel ? `<small class="tab-subtitle">${esc(g.sublabel)}</small>` : ''}</span></button>`).join('');
    const panels = result.groups.map((g) => `<section class="accessory-group accessory-panel" role="tabpanel" id="lsf-acc-panel-${g.key}" aria-labelledby="lsf-acc-tab-${g.key}" tabindex="0" ${state.accessoryTab === g.key ? '' : 'hidden'}><div class="accessory-group-heading"><h3>${esc(g.label)}</h3>${g.desc ? `<p>${esc(g.desc)}</p>` : ''}</div><div class="accessory-grid">${g.items.map((row) => accessoryCard(row, g)).join('')}</div></section>`).join('');
    accMount.innerHTML = `<div class="results-page"><section class="accessories-section" id="pairing-section" aria-labelledby="lsf-pairing-heading"><div class="accessories-header"><div>${t('acc_eyebrow') ? `<div class="eyebrow">${esc(t('acc_eyebrow'))}</div>` : ''}<h2 id="lsf-pairing-heading">${esc(t('acc_title'))}</h2><p>${esc(cap === null ? t('acc_text_open') : t('acc_text_cap', { cap: money(cap) }))}</p></div><span class="accessory-budget">${icon('wallet')}<span>${esc(cap === null ? t('acc_badge_open') : t('acc_badge_cap', { cap: money(cap) }))}</span></span></div>${
      result.groups.length ? `<div class="accessory-tabs" role="tablist" aria-label="${esc(t('acc_tabs_label'))}">${tabs}</div>${panels}` : `<p class="accessories-empty">${esc(t('acc_empty'))}</p>`
    }${t('acc_note') ? `<p class="accessories-note">${esc(t('acc_note'))}</p>` : ''}</section></div>`;
    markLoaded(accMount);
  }
  function selectAccessoryTab(category, focus = false) {
    const group = lastPairResult?.groups.find((g) => g.key === category);
    const section = document.getElementById('pairing-section');
    if (!group || !section) return;
    const changed = state.accessoryTab !== category;
    state.accessoryTab = category;
    section.querySelectorAll('[role="tab"]').forEach((tab) => {
      const selected = tab.dataset.category === category;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      const panel = document.getElementById(tab.getAttribute('aria-controls'));
      if (panel) panel.hidden = !selected;
      if (selected && focus) tab.focus({ preventScroll: true });
    });
    persist();
    if (changed) {
      announce(t('ann_acc', { group: group.label, n: group.items.length }));
      emit('accessory_category_change', { category, shown: group.items.length });
    }
  }

  /* ---------- Method ---------- */
  function renderMethod() {
    if (!methodMount) return;
    const show = CFG.method.showOn === 'always' || (state.screen === 'results' && ready);
    methodMount.innerHTML = show
      ? `<div class="results-page"><section class="method-card"><div><h2>${CFG.method.heading || ''}</h2>${hasAbout && t('method_button') ? `<button type="button" data-action="about">${esc(t('method_button'))}</button>` : ''}</div>${t('method_text') ? `<p>${esc(t('method_text'))}</p>` : ''}</section></div>`
      : '';
  }

  /* ---------- Dialogs ---------- */
  function openDialog(title, body) {
    if (!dialog) return;
    dialog.querySelector('#dialog-title').textContent = title;
    dialog.querySelector('#dialog-body').innerHTML = body;
    if (!dialog.open) dialog.showModal();
  }
  function showAbout() {
    const m = CFG.method || {};
    openDialog(t('about_title'), `${t('about_intro') ? `<p>${esc(t('about_intro'))}</p>` : ''}<table class="info-table">${(m.about || []).map((row) => `<tr><th>${esc(row.title)}</th><td>${esc(row.text)}</td></tr>`).join('')}</table>${t('about_note') ? `<div class="small-notice">${esc(t('about_note'))}</div>` : ''}`);
  }
  const sourceDisplay = (p, key) => C.values(p, key).map(vl).join(', ') || t('row_unknown');
  function productDetail(handle) {
    const r = lastResult?.eligible.find((x) => x.product.handle === handle);
    if (!r) return;
    const p = r.product;
    const rows = [
      [t('row_status'), fitLabel(r)],
      ...Object.keys(C.fields).filter((k) => C.values(p, k).length).map((k) => [C.labels[k], sourceDisplay(p, k)]),
      [t('row_group'), [p.signature, ...(p.secondary || [])].filter(Boolean).map(soundLabel).join(' / ') || t('row_unknown')],
      [t('row_config'), vl(p.configuration) || t('row_unknown')],
      [t('row_tech'), (p.technology || []).map(vl).join(', ') || t('row_unknown')],
      [t('row_required'), r.requiredMatches.map((x) => x.text).join('; ') || t('row_none')],
      [t('row_gaps'), r.gaps.map((x) => t('why_line', { facet: x.facet, text: x.text })).join('; ') || t('row_none')],
    ].filter((row) => row[0]);
    openDialog(p.name, `${t('detail_intro') ? `<p>${esc(t('detail_intro'))}</p>` : ''}${fitDisclosure(r)}<table class="info-table">${rows.map(([k, v]) => `<tr><th>${esc(k)}</th><td>${esc(v)}</td></tr>`).join('')}</table>${C.graphURL(p) && t('facts_graph') ? `<p style="margin-top:18px"><a href="${esc(C.graphURL(p))}" target="_blank" rel="noopener noreferrer">${esc(t('facts_graph'))} &#8599;</a></p>` : ''}<p style="margin-top:18px"><a href="${esc(p.url)}">${esc(t('detail_link'))}</a></p>`);
  }
  function compare() {
    if (!lastShortlist.length) return;
    const rows = [
      [t('compare_price'), (r) => money(chosenVariant(r).price)],
      [t('compare_option'), (r) => optionLabel(chosenVariant(r).label)],
      [t('row_status'), fitLabel],
      ...Object.keys(C.fields).filter((k) => lastShortlist.some((r) => C.values(r.product, k).length)).map((k) => [C.labels[k], (r) => sourceDisplay(r.product, k)]),
      [t('row_group'), (r) => [r.product.signature, ...(r.product.secondary || [])].filter(Boolean).map(soundLabel).join(' / ') || t('row_unknown')],
      [t('row_config'), (r) => vl(r.product.configuration) || t('row_unknown')],
      [t('row_tech'), (r) => (r.product.technology || []).map(vl).join(', ') || t('row_unknown')],
      [t('row_gaps'), (r) => r.gaps.map((g) => t('why_line', { facet: g.facet, text: g.text })).join('; ') || t('row_none')],
      [t('compare_check'), caution],
    ].filter((row) => row[0]);
    openDialog(t('compare_title'), `${t('compare_intro') ? `<p>${esc(t('compare_intro'))}</p>` : ''}<div class="table-scroll" tabindex="0" aria-label="${esc(t('compare_title'))}"><table class="info-table comparison"><thead><tr><th>${esc(t('compare_attribute'))}</th>${lastShortlist.map((r) => `<th>${esc(r.product.name)}</th>`).join('')}</tr></thead><tbody>${rows.map(([k, fn]) => `<tr><th scope="row">${esc(k)}</th>${lastShortlist.map((r) => `<td>${esc(fn(r))}</td>`).join('')}</tr>`).join('')}</tbody></table></div>${t('compare_note') ? `<div class="small-notice">${esc(t('compare_note'))}</div>` : ''}`);
    emit('compare_open');
  }

  /* ---------- Share links (finder-prefill.js) ---------- */
  function toURL() {
    const a = E.normalize(state.answers);
    const q = new URLSearchParams();
    q.set('scope', a.scope);
    const b = E.budgetBand(state.budgetChoice);
    if (b) q.set('band', b.id);
    else {
      q.set('minBudget', String(a.minBudget));
      if (a.maxBudget !== null) q.set('maxBudget', String(a.maxBudget));
    }
    if (a.tuningProfile) q.set('tuningProfile', a.tuningProfile);
    a.soundColors.forEach((v) => q.append('soundColor', v));
    if (a.signatures[0]) q.set('signature', a.signatures[0]);
    if (a.signatures[1]) q.set('also', a.signatures[1]);
    a.genres.forEach((v) => q.append('genre', v));
    a.uses.forEach((v) => q.append('use', v));
    a.technology.forEach((v) => q.append('technology', v));
    if (a.configuration) q.set('configuration', a.configuration);
    if (a.acoustic) q.set('acoustic', a.acoustic);
    if (a.strictBuild) q.set('strictBuild', '1');
    if (a.needsClosedBack) q.set('needsClosedBack', '1');
    if (a.requireComplete) q.set('requireComplete', '1');
    for (const k of Object.keys(C.fields)) if (a.categoryFilters[k]) q.set('category.' + k, a.categoryFilters[k]);
    if (state.screen === 'results') q.set('view', 'results');
    const url = new URL(window.location.href);
    url.hash = q.toString();
    return url.href;
  }
  function applyHash() {
    const q = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    if (![...q.keys()].length) return false;
    const uniq = (x) => [...new Set(x.filter(Boolean))];
    const a = E.defaults();
    a.scope = Q.showHeadphones && q.get('scope') === 'headphones' ? 'headphones' : 'iems';
    a.signatures = uniq([q.get('signature'), q.get('also')]);
    a.tuningProfile = q.get('tuningProfile') || '';
    a.soundColors = uniq(q.getAll('soundColor'));
    a.genres = uniq(q.getAll('genre'));
    a.uses = uniq(q.getAll('use'));
    a.technology = uniq(q.getAll('technology'));
    a.configuration = q.get('configuration') || '';
    a.acoustic = q.get('acoustic') || '';
    a.strictBuild = q.get('strictBuild') === '1';
    a.needsClosedBack = q.get('needsClosedBack') === '1';
    a.requireComplete = q.get('requireComplete') === '1';
    for (const k of Object.keys(C.fields)) { const v = q.get('category.' + k); if (v) a.categoryFilters[k] = v; }
    const band = E.budgetBand(q.get('band'));
    let budgetChoice = '';
    if (band) { a.minBudget = band.min; a.maxBudget = band.max; budgetChoice = band.id; }
    else if (q.has('minBudget') || q.has('maxBudget')) {
      const number = (s) => (s !== null && /^(?:\d+(?:\.\d{1,2})?)$/.test(s) ? Number(s) : NaN);
      const min = q.has('minBudget') ? number(q.get('minBudget')) : 0;
      const max = q.has('maxBudget') ? number(q.get('maxBudget')) : null;
      try { E.validateBudget({ minBudget: min, maxBudget: max }); a.minBudget = min; a.maxBudget = max; budgetChoice = 'custom'; } catch (_) {}
    }
    const n = E.normalize(a);
    const results = q.get('view') === 'results' && Boolean(budgetChoice);
    state = {
      ...initial(), answers: n, screen: results ? 'results' : 'quiz', budgetChoice,
      genreAny: !n.genres.length, signatureAny: !n.tuningProfile && !n.soundColors.length && !n.signatures.length, useAny: !n.uses.length,
      buildMode: n.technology.length || n.configuration || n.acoustic || Object.keys(n.categoryFilters).length ? 'advanced' : 'auto',
      completed: results ? [0, 1, 2, 3, 4] : [], notice: results ? '' : t('prefill_notice'),
    };
    return true;
  }
  async function shareChoices() {
    const url = toURL();
    let copied = false;
    try {
      if (navigator.clipboard) { await navigator.clipboard.writeText(url); copied = true; }
    } catch (_) {}
    if (copied) { announce(t('share_copied')); flash(t('share_copied')); return; }
    openDialog(t('share_title'), `${t('share_text') ? `<p>${esc(t('share_text'))}</p>` : ''}<label class="finder-share-label" for="lsf-share-url">${esc(t('share_field'))}</label><input id="lsf-share-url" class="finder-share-url" readonly value="${esc(url)}">`);
    const field = document.getElementById('lsf-share-url');
    field.focus();
    field.select();
  }
  function flash(text) {
    const button = resultsMount?.querySelector('[data-action="share-finder"]');
    if (!button || !text) return;
    const original = button.textContent;
    button.textContent = text;
    setTimeout(() => { button.textContent = original; }, 2000);
  }

  /* ---------- Render ---------- */
  function markLoaded(scope) {
    scope.querySelectorAll('img').forEach((img) => { if (img.complete && img.naturalWidth) img.classList.add('loaded'); });
  }
  function render(focusKey) {
    state.answers = E.normalize(state.answers);
    delete state.answers.categoryFilters.driverConfiguration;
    const results = state.screen === 'results';
    quizMount.hidden = results;
    if (resultsRoot) resultsRoot.hidden = !results;
    if (accRoot) accRoot.hidden = !results;
    if (results) {
      quizMount.innerHTML = '';
      renderResults();
    } else {
      if (resultsMount) resultsMount.innerHTML = '';
      renderQuiz();
    }
    renderAccessories();
    renderMethod();
    persist();
    if (focusKey) {
      const target = document.querySelector(`.ls-finder [data-focus="${CSS.escape(focusKey)}"]`);
      if (target) target.focus({ preventScroll: true });
    }
    roots.forEach(markLoaded);
  }
  function animateIn(selector, stagger) {
    const g = window.gsap;
    if (!g || !motionOK) return;
    const targets = document.querySelectorAll(selector);
    if (targets.length) g.fromTo(targets, { autoAlpha: 0, y: 12 }, { autoAlpha: 1, y: 0, duration: 0.42, ease: 'power3.out', stagger: stagger || 0, clearProps: 'opacity,visibility,transform' });
  }
  function focusHeading() {
    const h = document.getElementById('question-title');
    if (h) h.focus({ preventScroll: true });
    const top = (state.screen === 'results' ? resultsRoot : quizRoot) || quizRoot;
    const y = top.getBoundingClientRect().top + window.scrollY - (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--ls-header-offset')) || 0) - 16;
    if (window.scrollY > y) window.scrollTo({ top: Math.max(0, y), behavior: motionOK ? 'smooth' : 'instant' });
    if (state.screen === 'results') animateIn('.ls-finder .products-grid > .product-card', 0.07);
    else animateIn('.ls-finder .quiz-card .question-body, .ls-finder .quiz-card h1, .ls-finder .quiz-card .question-desc', 0.04);
  }
  function edit(n) {
    state.step = n;
    state.screen = 'quiz';
    state.editing = true;
    state.showAll = false;
    render();
    focusHeading();
  }

  /* ---------- Events ---------- */
  function inputChange(target) {
    const bind = target.dataset.bind;
    const v = target.value;
    const key = target.dataset.focus;
    const a = state.answers;
    if (bind === 'scope') {
      a.scope = v;
      a.configuration = '';
      a.technology = [];
      a.acoustic = '';
      a.strictBuild = false;
      a.needsClosedBack = false;
      state.showUnavailable = false;
      const removed = [];
      if (ready) {
        const universe = E.rank(DATA.products, { ...E.defaults(), scope: v }).eligible.map((r) => r.product);
        if (a.tuningProfile && !universe.some((p) => C.values(p, 'tuningProfile').includes(a.tuningProfile))) { removed.push(C.labels.tuningProfile + ': ' + a.tuningProfile); a.tuningProfile = ''; }
        a.soundColors = a.soundColors.filter((x) => { const keep = universe.some((p) => C.values(p, 'soundColor').includes(x)); if (!keep) removed.push(C.labels.soundColor + ': ' + x); return keep; });
        for (const [k, x] of Object.entries(a.categoryFilters)) if (!universe.some((p) => C.matches(p, { [k]: x }))) { removed.push(C.labels[k] + ': ' + x); delete a.categoryFilters[k]; }
      }
      state.notice = t('scope_notice', { scope: scopeLabel(v) }) + (removed.length ? ' ' + t('scope_notice_removed', { list: removed.join('; ') }) : '');
      render(key);
      announce(state.notice);
      return;
    }
    if (bind === 'budget') {
      const band = E.budgetBand(v);
      if (!band) return;
      a.minBudget = band.min;
      a.maxBudget = band.max;
      state.budgetChoice = band.id;
      variantChoices = {};
    }
    if (bind === 'genre') {
      if (target.checked) {
        if (a.genres.length >= 3) {
          target.checked = false;
          announce(t('genre_limit'));
          const err = document.getElementById('input-error');
          if (err) err.textContent = t('genre_limit');
          return;
        }
        a.genres.push(v);
      } else a.genres = a.genres.filter((g) => g !== v);
      state.genreAny = false;
    }
    if (bind === 'setup') {
      if (a.scope === 'headphones') a.acoustic = v;
      else {
        a.configuration = v;
        if (!v) a.technology = [];
        else {
          const limit = E.SETUP_TECH_LIMIT[v] || 0;
          if (limit && a.technology.length > limit) a.technology = a.technology.slice(-limit);
        }
      }
    }
    if (bind === 'closed-back') a.needsClosedBack = target.checked;
    if (bind === 'technology') a.technology = target.checked ? nextTech(v) : a.technology.filter((x) => x !== v);
    if (bind === 'setup' || bind === 'technology') {
      a.strictBuild = Boolean(a.configuration || a.technology.length || a.acoustic);
      state.buildMode = a.strictBuild ? 'advanced' : 'auto';
    }
    if (bind === 'signature') {
      a.tuningProfile = '';
      a.soundColors = [];
      if (!setMulti('signatures', v, target.checked, target)) return;
      state.signatureAny = false;
    }
    if (bind === 'tuning-profile') {
      a.tuningProfile = v;
      a.signatures = [];
      a.signature = '';
      state.signatureAny = !v && !a.soundColors.length;
    }
    if (bind === 'sound-color') {
      if (target.checked && a.soundColors.length >= 2) {
        target.checked = false;
        announce(t('color_limit'));
        const err = document.getElementById('input-error');
        if (err) err.textContent = t('color_limit');
        return;
      }
      a.soundColors = target.checked ? [...a.soundColors, v] : a.soundColors.filter((x) => x !== v);
      a.signatures = [];
      a.signature = '';
      state.signatureAny = !a.soundColors.length && !a.tuningProfile;
    }
    if (bind === 'require-complete') a.requireComplete = target.checked;
    if (bind === 'category-filter') {
      if (v) a.categoryFilters[target.dataset.category] = v;
      else delete a.categoryFilters[target.dataset.category];
    }
    if (bind === 'use') {
      if (!setMulti('uses', v, target.checked, target)) return;
      state.useAny = false;
    }
    if (bind === 'variant') {
      const r = lastResult?.eligible.find((x) => x.product.handle === target.dataset.handle);
      const vv = r?.variants[Number(v)];
      if (vv) {
        variantChoices[r.product.handle] = Number(v);
        const price = document.getElementById('lsf-price-' + r.product.handle);
        if (price) price.innerHTML = (t('card_price') ? `<small>${esc(t('card_price'))}</small>` : '') + esc(money(vv.price));
        const printed = document.getElementById('lsf-variant-print-' + r.product.handle);
        if (printed) printed.textContent = optionLabel(vv.label);
        announce(t('ann_price', { price: money(vv.price) }));
      }
      return;
    }
    render(key);
    emit('answer_change', { step: STEPS[state.step], field: bind });
  }

  document.addEventListener('change', (e) => {
    const el = e.target;
    if (el.dataset?.bind && el.closest('.ls-finder')) inputChange(el);
  });
  document.addEventListener('click', (e) => {
    if (!e.target.closest?.('.ls-finder')) return;
    const track = e.target.closest('[data-track-product]');
    if (track) emit('product_click', { handle: track.dataset.trackProduct });
    const pair = e.target.closest('[data-track-pairing]');
    if (pair) emit('pairing_click', { handle: pair.dataset.trackPairing, main: lastShortlist[0]?.product.handle });
    const b = e.target.closest('[data-action]');
    if (!b || b.disabled) return;
    const action = b.dataset.action;
    const a = state.answers;
    if (action === 'accessory-tab') selectAccessoryTab(b.dataset.category);
    else if (action === 'about') showAbout();
    else if (action === 'show-unavailable') { state.showUnavailable = !state.showUnavailable; render(); }
    else if (action === 'make-main') {
      a[b.dataset.field].reverse();
      a.signature = a.signatures[0] || '';
      a.use = a.uses[0] || '';
      render();
      announce(t('ann_main'));
    } else if (action === 'share-finder') shareChoices();
    else if (action === 'allow-partial') { a.requireComplete = false; render(); focusHeading(); announce(t('ann_partial')); }
    else if (action === 'close-dialog') dialog?.close();
    else if (action === 'next') {
      if (!valid()) return;
      if (!state.completed.includes(state.step)) state.completed.push(state.step);
      state.completed.sort();
      emit('step_complete', { step: STEPS[state.step] });
      state.notice = '';
      if (state.editing || state.step === 4) { state.screen = 'results'; state.editing = false; }
      else state.step++;
      render();
      focusHeading();
    } else if (action === 'back') { state.step = Math.max(0, state.step - 1); render(); focusHeading(); }
    else if (action === 'step') { state.step = Number(b.dataset.step); render(); focusHeading(); }
    else if (action === 'edit-step') edit(Number(b.dataset.step));
    else if (action === 'change-scope') { edit(0); quizRoot.querySelector('.scope-choice input:checked')?.focus(); }
    else if (action === 'reset') { state = initial(); variantChoices = {}; render(); focusHeading(); announce(t('ann_reset')); emit('restart'); }
    else if (action === 'genre-any') { a.genres = []; state.genreAny = true; render('genre-any'); }
    else if (action === 'signature-any') { a.tuningProfile = ''; a.soundColors = []; a.signatures = []; a.signature = ''; state.signatureAny = true; render('signature-any'); }
    else if (action === 'use-any') { a.uses = []; a.use = ''; state.useAny = true; render('use-any'); }
    else if (action === 'compare') compare();
    else if (action === 'print') { emit('print_shortlist'); window.print(); }
    else if (action === 'product-detail') productDetail(b.dataset.handle);
    else if (action === 'show-more') { state.showAll = !state.showAll; render(); }
    else if (action === 'load-more') { state.moreLimit += 12; render(); }
    else if (action === 'relax-build') { a.strictBuild = false; render(); focusHeading(); announce(t('ann_relaxed')); }
    else if (action === 'browse-budget') {
      a.requireComplete = false; a.tuningProfile = ''; a.soundColors = []; a.genres = []; state.genreAny = true;
      a.signatures = []; a.signature = ''; state.signatureAny = true; a.uses = []; a.use = ''; state.useAny = true;
      if (!a.strictBuild) { a.configuration = ''; a.technology = []; a.acoustic = ''; }
      render(); focusHeading(); announce(t('ann_cleared'));
    }
  });
  document.addEventListener('keydown', (e) => {
    const tab = e.target.closest?.('.ls-finder .accessory-tab[role="tab"]');
    if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
    const tabs = Array.from(tab.closest('[role="tablist"]').querySelectorAll('[role="tab"]'));
    const index = tabs.indexOf(tab);
    const next = e.key === 'Home' ? 0 : e.key === 'End' ? tabs.length - 1 : (index + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    e.preventDefault();
    selectAccessoryTab(tabs[next].dataset.category, true);
  });
  document.addEventListener('load', (e) => { if (e.target.tagName === 'IMG' && e.target.closest('.ls-finder')) e.target.classList.add('loaded'); }, true);
  document.addEventListener('error', (e) => { if (e.target.tagName === 'IMG' && e.target.closest('.ls-finder')) { e.target.style.display = 'none'; e.target.removeAttribute('src'); } }, true);
  dialog?.addEventListener('click', (e) => {
    if (e.target !== dialog) return;
    const r = dialog.getBoundingClientRect();
    if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) dialog.close();
  });
  window.addEventListener('hashchange', () => { if (applyHash()) { render(); focusHeading(); } });

  window.LsFinder = {
    getState: () => JSON.parse(JSON.stringify(state)),
    getData: () => DATA,
    getResults: () => lastResult,
    reset: () => { state = initial(); variantChoices = {}; render(); },
  };

  /* ---------- Start ---------- */
  applyHash();
  quizRoot.classList.add('is-ready');
  render();
  emit('finder_start');
  loadFeed(Q.collectionUrl || '/collections/all')
    .then((items) => {
      DATA.products = items.map(productRecord).filter(Boolean);
      ready = true;
      const active = document.activeElement?.dataset?.focus;
      render(active);
    })
    .catch(() => {
      failed = true;
      render();
    });
})();
